/**
 * Central de Notas
 *
 * Hub de Notas Fiscais / Notas de Débito com filtros, anotações e exportação.
 */

import { useState, useMemo, useCallback, useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '@/hooks/useAuth'
import { usePageTitle } from '@/stores/page-title-store'
import { SensitiveValue } from '@/components/privacy/SensitiveText'
import { EnhancedPagination } from '@/components/common/EnhancedPagination'
import { useCardNotesStore, NOTES_ALLOWED_ROLES, type CardNote } from '@/stores/card-notes-store'
import {
  PeriodPresetSelector,
  calculatePeriodDates,
  type PeriodDates,
} from '../dre/components/PeriodPresetSelector'
import {
  Shield, Download, Search, X,
  ChevronDown, Check, Save, Trash2, Plus, Settings2,
  MessageSquare, Pencil,
  FileText, Mail, Calendar, ArrowRight, TrendingDown, User,
  Send, AlertTriangle, HelpCircle,
  LayoutDashboard, Wallet, FileSignature, Clock, PieChart,
} from 'lucide-react'
import { useSelicRate } from './useSelicRate'
import {
  MOCK_NOTAS,
  type NotaFiscal,
  type NotaStatus,
} from './mock-data'
import { MOCK_CONTRATOS, type Contrato } from '../contratos/mock-data'

// ============================================================================
// CONSTANTS
// ============================================================================

const PAGE_SIZE = 20

const STATUS_LABELS: Record<NotaStatus, string> = {
  pago: 'Paga',
  aberto: 'Em Aberto',
  vencido: 'Vencida',
  cancelada: 'Cancelada',
}

const STATUS_COLORS: Record<NotaStatus, string> = {
  pago: 'bg-transparent text-brand-primary border-brand-primary/50',
  aberto: 'bg-transparent text-white border-white/50',
  vencido: 'bg-red-500/20 text-red-400 border-red-500/30',
  cancelada: 'bg-transparent text-brand-gray border-brand-gray/40',
}

// ============================================================================
// COLUMN DEFINITIONS
// ============================================================================

interface ColumnDef<T> {
  key: string
  label: string
  render: (row: T) => ReactNode
  align?: 'left' | 'right' | 'center'
  width?: string
}

type SortDirection = 'asc' | 'desc'
interface SortState {
  key: string
  direction: SortDirection
}

/** Resolve sort value for a row — handles computed columns like diasAtraso. */
function getSortValue<T>(row: T, key: string): unknown {
  const record = row as Record<string, unknown>
  if (key === 'diasAtraso') {
    // Paid/cancelled rows have no atraso; otherwise compute from dataVencimento
    if (record.status === 'pago' || record.status === 'cancelada') return null
    const venc = record.dataVencimento
    if (typeof venc !== 'string') return null
    const dias = calcDiasAtraso(venc)
    return dias > 0 ? dias : null
  }
  return record[key]
}

/** Generic sort for table rows. Handles strings, numbers, dates, and nulls. */
function sortRows<T>(rows: T[], sort: SortState | null): T[] {
  if (!sort) return rows
  const { key, direction } = sort
  const mult = direction === 'asc' ? 1 : -1

  return [...rows].sort((a, b) => {
    const aVal = getSortValue(a, key)
    const bVal = getSortValue(b, key)

    // nulls/undefined always last
    if (aVal == null && bVal == null) return 0
    if (aVal == null) return 1
    if (bVal == null) return -1

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return (aVal - bVal) * mult
    }

    const aStr = String(aVal)
    const bStr = String(bVal)
    return aStr.localeCompare(bStr, 'pt-BR', { numeric: true }) * mult
  })
}

function toggleSort(current: SortState | null, key: string): SortState | null {
  if (!current || current.key !== key) return { key, direction: 'asc' }
  if (current.direction === 'asc') return { key, direction: 'desc' }
  return null // third click removes sort
}

/** Sort indicator arrow */
function SortIndicator({ sortState, columnKey }: { sortState: SortState | null; columnKey: string }) {
  if (!sortState || sortState.key !== columnKey) {
    return <span className="text-brand-gray/30 ml-1">↕</span>
  }
  return <span className="text-brand-primary ml-1">{sortState.direction === 'asc' ? '↑' : '↓'}</span>
}

const NOTA_COLUMNS: ColumnDef<NotaFiscal>[] = [
  {
    key: 'tipo',
    label: 'Tipo',
    width: 'w-20',
    render: (row) => (
      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${
        row.tipo === 'NF'
          ? 'bg-brand-primary/20 text-brand-primary border-brand-primary/30'
          : 'bg-brand-dark-lighter text-brand-gray border-brand-dark-lighter'
      }`}>
        {row.tipo}
      </span>
    ),
  },
  {
    key: 'dataEmissao',
    label: 'Emissão',
    width: 'w-28',
    render: (row) => formatDateBR(row.dataEmissao),
  },
  {
    key: 'numero',
    label: 'Número',
    width: 'w-32',
    render: (row) => <span className="text-xs">{row.numero}</span>,
  },
  {
    key: 'cliente',
    label: 'Cliente',
    render: (row) => <span className="text-white font-medium">{row.cliente}</span>,
  },
  {
    key: 'favorecido',
    label: 'Favorecido',
    render: (row) => <span className="text-white">{row.favorecido}</span>,
  },
  {
    key: 'cnpj',
    label: 'CNPJ',
    width: 'w-40',
    render: (row) => <span className="text-xs">{row.cnpj}</span>,
  },
  {
    key: 'contrato',
    label: 'Contrato nº',
    width: 'w-24',
    render: (row) => (
      <span className={row.contrato ? '' : 'text-brand-gray/40 italic'}>
        {row.contrato || '—'}
      </span>
    ),
  },
  {
    key: 'tipoServico',
    label: 'Serviço',
    render: (row) => row.tipoServico,
  },
  {
    key: 'servicoTipo',
    label: 'Tipo de Serviço',
    width: 'w-32',
    render: (row) => (
      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-transparent text-brand-gray border border-brand-gray/30">
        {row.servicoTipo === 'digital' ? 'DIGITAL' : 'PROMO'}
      </span>
    ),
  },
  {
    key: 'pit',
    label: 'PIT',
    width: 'w-24',
    render: (row) => <span className="text-xs">{row.pit}</span>,
  },
  {
    key: 'digitalPromo',
    label: 'Tipo de Contrato',
    width: 'w-32',
    render: (row) => (
      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-transparent text-brand-gray border border-brand-gray/30">
        {row.digitalPromo === 'digital' ? 'DIGITAL' : 'PROMO'}
      </span>
    ),
  },
  {
    key: 'destinatario',
    label: 'Destinatário',
    render: (row) => (
      <span className="text-xs text-brand-gray-light truncate block" title={row.destinatario}>
        {row.destinatario}
      </span>
    ),
  },
  {
    key: 'valorBruto',
    label: 'Valor Bruto',
    width: 'w-32',
    render: (row) => (
      <SensitiveValue mask="R$ ••••••">
        <span className="font-medium text-white">
          {formatCurrencyBR(row.valorBruto)}
        </span>
      </SensitiveValue>
    ),
  },
  {
    key: 'valorLiquido',
    label: 'Valor Líquido',
    width: 'w-32',
    render: (row) => (
      <SensitiveValue mask="R$ ••••••">
        <span className="font-medium text-white">
          {formatCurrencyBR(row.valorLiquido)}
        </span>
      </SensitiveValue>
    ),
  },
  {
    key: 'retencao',
    label: 'Retenção',
    width: 'w-24',
    render: (row) => {
      if (!row.valorBruto) return <span className="text-brand-gray/40">—</span>
      const pct = ((row.valorBruto - row.valorLiquido) / row.valorBruto) * 100
      return (
        <span className="text-xs text-brand-gray-light">
          {pct.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
        </span>
      )
    },
  },
  {
    key: 'dataVencimento',
    label: 'Vencimento',
    width: 'w-28',
    render: (row) => formatDateBR(row.dataVencimento),
  },
  {
    key: 'dataBaixa',
    label: 'Baixa',
    width: 'w-28',
    render: (row) => row.dataBaixa ? formatDateBR(row.dataBaixa) : <span className="text-brand-gray/40">—</span>,
  },
  {
    key: 'diasAtraso',
    label: 'Dias Atraso',
    width: 'w-28',
    render: (row) => {
      if (row.status === 'pago' || row.status === 'cancelada') return <span className="text-brand-gray/40">—</span>
      const dias = calcDiasAtraso(row.dataVencimento)
      if (dias <= 0) return <span className="text-brand-gray/40">—</span>
      return <span className="font-medium text-red-400">{dias}d</span>
    },
  },
  {
    key: 'status',
    label: 'Status',
    width: 'w-28',
    render: (row) => (
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_COLORS[row.status]}`}>
        {STATUS_LABELS[row.status]}
      </span>
    ),
  },
  {
    key: 'anotacoes',
    label: 'Anotações',
    width: 'w-24',
    render: (row) => <AnnotationsCountCell id={row.id} />,
  },
]


// ============================================================================
// COLUMN PRESETS
// ============================================================================

interface ColumnPreset {
  id: string
  name: string
  columns: string[]
  isDefault?: boolean
}

const DEFAULT_NOTA_PRESETS: ColumnPreset[] = [
  {
    id: 'completa',
    name: 'Completa',
    columns: NOTA_COLUMNS.map((c) => c.key),
    isDefault: true,
  },
  {
    id: 'financeira',
    name: 'Financeira',
    columns: ['tipo', 'dataEmissao', 'numero', 'cliente', 'valorBruto', 'valorLiquido', 'retencao', 'dataBaixa', 'diasAtraso', 'status', 'anotacoes'],
  },
  {
    id: 'contratual',
    name: 'Contratual',
    columns: ['tipo', 'numero', 'cliente', 'contrato', 'pit', 'tipoServico', 'servicoTipo', 'digitalPromo', 'destinatario'],
  },
]

// ============================================================================
// FORMATTERS
// ============================================================================

function formatCurrencyBR(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function formatDateBR(dateStr: string): string {
  const [y, m, d] = dateStr.split('-')
  return `${d}/${m}/${y}`
}

/** Calcula dias de atraso em relação ao dia de hoje. Retorna <= 0 quando ainda não venceu. */
function calcDiasAtraso(dataVencimento: string): number {
  const [y, m, d] = dataVencimento.split('-').map(Number)
  const venc = new Date(y, m - 1, d)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.floor((today.getTime() - venc.getTime()) / (1000 * 60 * 60 * 24))
}

function formatCompact(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `R$ ${(value / 1_000_000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`
  if (abs >= 1_000) return `R$ ${(value / 1_000).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}K`
  return formatCurrencyBR(value)
}

/** Clamp a dropdown's left position so it doesn't overflow the viewport. */
function clampDropdownLeft(left: number, width: number, margin = 8): number {
  if (typeof window === 'undefined') return left
  const maxLeft = window.innerWidth - width - margin
  return Math.max(margin, Math.min(left, maxLeft))
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/** Modal overlay explaining a KPI calculation. */
function KpiHelpModal({ title, content, onClose }: {
  title: string
  content: string
  onClose: () => void
}) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#1E2024] border border-[#2A2D34] rounded-xl max-w-md w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-[#2A2D34]">
          <div className="flex items-center gap-2">
            <HelpCircle size={16} className="text-brand-primary" />
            <h3 className="text-white font-medium text-sm">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-brand-gray hover:text-white transition-colors cursor-pointer"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-4 text-sm text-brand-gray-light leading-relaxed whitespace-pre-line">
          {content}
        </div>
      </div>
    </div>
  )
}

/** Help button for KPI cards — opens an explanation modal on click. */
function KpiHelp({ title, content }: { title: string; content: string }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true) }}
        className="text-brand-gray/50 hover:text-brand-gray transition-colors cursor-pointer p-0.5"
        aria-label="Ver explicação"
        title="Ver explicação"
      >
        <HelpCircle size={13} />
      </button>
      {open && createPortal(
        <KpiHelpModal title={title} content={content} onClose={() => setOpen(false)} />,
        document.body,
      )}
    </>
  )
}

/** Generic KPI card. Children render the value/visualization freely. */
function KpiCard({ label, help, headerRight, children }: {
  label: string
  help?: string
  headerRight?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="metric-card relative flex flex-col">
      {help && (
        <div className="absolute top-3 right-3">
          <KpiHelp title={label} content={help} />
        </div>
      )}
      <div className="flex items-center justify-between pr-6">
        <span className="text-xs font-medium text-brand-gray uppercase tracking-wide">{label}</span>
        {headerRight}
      </div>
      <div className="mt-3 flex-1">{children}</div>
    </div>
  )
}

/** Status card — compact wrapper for the "pie slice" style cards. */
function SummaryCard({ label, value, count, color = 'text-white', help }: {
  label: string
  value: number
  count: number
  color?: string
  help?: string
}) {
  return (
    <KpiCard label={label} help={help}>
      <div className={`text-xl lg:text-2xl font-bold font-display ${color}`}>
        <SensitiveValue mask="R$ ••••••">
          {formatCompact(value)}
        </SensitiveValue>
      </div>
      <p className="text-brand-gray/40 text-[10px] mt-0.5">
        <SensitiveValue mask="R$ ••••••">{formatCurrencyBR(value)}</SensitiveValue>
      </p>
      <p className="text-brand-gray/60 text-xs mt-1.5">{count} {count === 1 ? 'nota' : 'notas'}</p>
    </KpiCard>
  )
}

/** Underline-style tab bar for KPI sections. */
const KPI_TABS = [
  { id: 'geral', label: 'Visão Geral', icon: LayoutDashboard },
  { id: 'recebimentos', label: 'Recebimentos', icon: Wallet },
  { id: 'composicao', label: 'Composição', icon: PieChart },
  { id: 'execucao', label: 'Execução Contratual', icon: FileSignature },
  { id: 'ciclo', label: 'Ciclo Financeiro', icon: Clock },
] as const

type KpiTabId = typeof KPI_TABS[number]['id']

function KpiTabBar({ active, onChange, badges }: {
  active: KpiTabId
  onChange: (tab: KpiTabId) => void
  badges?: Partial<Record<KpiTabId, number>>
}) {
  return (
    <div className="flex items-center gap-6 border-b border-brand-dark-lighter overflow-x-auto overflow-y-hidden">
      {KPI_TABS.map((t) => {
        const badge = badges?.[t.id] ?? 0
        return (
          <button
            key={t.id}
            onClick={() => onChange(t.id)}
            className={`pb-2 -mb-px text-sm font-medium transition-colors border-b-2 whitespace-nowrap cursor-pointer flex items-center gap-2 ${
              active === t.id
                ? 'text-white border-brand-primary'
                : 'text-brand-gray border-transparent hover:text-brand-gray-light'
            }`}
          >
            <t.icon size={14} />
            {t.label}
            {badge > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-400 text-white text-[10px] font-semibold leading-none">
                {badge}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Wrapper that animates tab content with a "curtain" effect:
 * - when the active tab changes, the current block collapses in height
 *   (pushing elements below upward);
 * - after the collapse, content is swapped while height is 0;
 * - then the new block expands, pushing elements below downward.
 *
 * Uses the CSS `grid-template-rows: 1fr/0fr` trick so height can animate
 * without measuring children.
 */
function AnimatedKpiTabContent({ activeTab, render }: {
  activeTab: KpiTabId
  render: (tab: KpiTabId) => ReactNode
}) {
  const [displayTab, setDisplayTab] = useState<KpiTabId>(activeTab)
  const [open, setOpen] = useState(true)

  // Ref mirror of displayTab — used in the guard below so we can keep
  // `displayTab` out of the effect deps (otherwise the setDisplayTab call
  // inside the timer would trigger a re-run and the cleanup would cancel
  // the reopening requestAnimationFrames).
  const displayTabRef = useRef(displayTab)
  useEffect(() => {
    displayTabRef.current = displayTab
  }, [displayTab])

  useEffect(() => {
    if (activeTab === displayTabRef.current) return

    // Phase 1: collapse current tab (pushes elements below upward)
    setOpen(false)
    let raf1 = 0
    let raf2 = 0
    const collapseTimer = window.setTimeout(() => {
      // Phase 2: swap content while still collapsed
      setDisplayTab(activeTab)
      // Phase 3: wait 2 frames so the browser paints at 0fr before
      // animating to 1fr (otherwise the transition is skipped).
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setOpen(true))
      })
    }, 220)

    return () => {
      clearTimeout(collapseTimer)
      if (raf1) cancelAnimationFrame(raf1)
      if (raf2) cancelAnimationFrame(raf2)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  return (
    <div className={`kpi-curtain ${open ? '' : 'kpi-curtain-closed'}`}>
      <div className="kpi-curtain-inner">
        {render(displayTab)}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers for KPI calculations
// ---------------------------------------------------------------------------

/** Days between two YYYY-MM-DD dates (b - a). */
function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number)
  const [by, bm, bd] = b.split('-').map(Number)
  const da = new Date(ay, am - 1, ad).getTime()
  const db = new Date(by, bm - 1, bd).getTime()
  return Math.round((db - da) / (1000 * 60 * 60 * 24))
}

/** Format signed days as "X dias" string. */
function formatDias(days: number): string {
  const rounded = Math.round(days)
  return `${rounded} ${Math.abs(rounded) === 1 ? 'dia' : 'dias'}`
}

// ---------------------------------------------------------------------------
// Ciclo Financeiro Cards (DSO + Custo do Ciclo)
// ---------------------------------------------------------------------------

const PREVISAO_WINDOWS = [7, 15, 30, 60] as const
type PrevisaoWindow = typeof PREVISAO_WINDOWS[number]

function CicloFinanceiroCards({ notas, cicloFilter, onCicloFilterChange }: {
  notas: NotaFiscal[]
  cicloFilter: 'adiantamentos' | 'atrasos' | null
  onCicloFilterChange: (v: 'adiantamentos' | 'atrasos' | null) => void
}) {
  const selic = useSelicRate()

  const metrics = useMemo(() => {
    const pagas = notas.filter((n) => n.status === 'pago' && n.dataBaixa)

    // DSO: média de dias entre emissão e baixa
    const diasCiclo = pagas.map((n) => daysBetween(n.dataEmissao, n.dataBaixa!))
    const dso = diasCiclo.length > 0 ? diasCiclo.reduce((a, b) => a + b, 0) / diasCiclo.length : 0

    const dailyRatePct = selic.daily / 100

    // Custo financeiro do ciclo (dentro do prazo): soma do valor × dias dentro do prazo × taxa diária
    let custoCiclo = 0
    const hojeIso = new Date().toISOString().split('T')[0]
    for (const nota of notas) {
      if (nota.status === 'cancelada') continue
      const vencDate = nota.dataVencimento
      const baixaDate = nota.dataBaixa
      const fimCicloDate = baixaDate
        ? (baixaDate < vencDate ? baixaDate : vencDate)
        : (hojeIso < vencDate ? hojeIso : vencDate)
      const diasCicloNota = Math.max(0, daysBetween(nota.dataEmissao, fimCicloDate))
      custoCiclo += nota.valorLiquido * diasCicloNota * dailyRatePct
    }

    return { dso, custoCiclo }
  }, [notas, selic.daily])

  // --- Custos ocultos ---
  const custosOcultos = useMemo(() => {
    const dailyRate = selic.daily / 100
    const hojeISO = new Date().toISOString().split('T')[0]

    let custoAtrasos = 0
    let qtdAtrasos = 0
    let somaDiasAtraso = 0

    let custoAdiantamentos = 0
    let qtdAdiantamentos = 0
    let somaDiasAdiantamento = 0
    let totalAdiantado = 0

    for (const nota of notas) {
      if (nota.status === 'cancelada') continue

      // Custo dos atrasos (dias após vencimento)
      const diasAtraso = Math.max(0, daysBetween(nota.dataVencimento, nota.dataBaixa ?? hojeISO))
      if (diasAtraso > 0 && (nota.status === 'vencido' || (nota.status === 'pago' && nota.dataBaixa && nota.dataBaixa > nota.dataVencimento))) {
        custoAtrasos += nota.valorLiquido * diasAtraso * dailyRate
        somaDiasAtraso += diasAtraso
        qtdAtrasos += 1
      }

      // Custo dos adiantamentos (dias entre pagamento do adiantamento e emissão da nota)
      if (nota.adiantamento) {
        const diasAdiant = Math.max(0, daysBetween(nota.adiantamento.dataPagamento, nota.dataEmissao))
        if (diasAdiant > 0) {
          custoAdiantamentos += nota.adiantamento.valor * diasAdiant * dailyRate
          somaDiasAdiantamento += diasAdiant
          qtdAdiantamentos += 1
          totalAdiantado += nota.adiantamento.valor
        }
      }
    }

    const atrasoMedioDias = qtdAtrasos > 0 ? somaDiasAtraso / qtdAtrasos : 0
    const adiantamentoMedioDias = qtdAdiantamentos > 0 ? somaDiasAdiantamento / qtdAdiantamentos : 0
    const custoTotal = metrics.custoCiclo + custoAtrasos + custoAdiantamentos

    return {
      custoAtrasos, qtdAtrasos, atrasoMedioDias,
      custoAdiantamentos, qtdAdiantamentos, adiantamentoMedioDias, totalAdiantado,
      custoTotal,
    }
  }, [notas, selic.daily, metrics.custoCiclo])

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <KpiCard
        label="Custo financeiro — Emissão/Baixa"
        help={`Custo de oportunidade do dinheiro parado entre emissão e baixa (ou vencimento, o que vier primeiro). Taxa diária SELIC de ${selic.daily.toFixed(4)}% a.d. (anual ${selic.annual.toFixed(2)}% — fonte: ${selic.source}).`}
      >
        <div className="text-xl lg:text-2xl font-bold font-display text-red-400">
          <SensitiveValue mask="R$ ••••••">{formatCurrencyBR(metrics.custoCiclo)}</SensitiveValue>
        </div>
        <p className="text-brand-gray/60 text-xs mt-2">
          Média {formatDias(metrics.dso)} · {notas.filter((n) => n.status === 'pago').length} notas
        </p>
      </KpiCard>

      <div
        onClick={() => onCicloFilterChange(cicloFilter === 'adiantamentos' ? null : 'adiantamentos')}
        className={`cursor-pointer transition-all [&>.metric-card]:transition-all ${cicloFilter === 'adiantamentos' ? '[&>.metric-card]:border-red-400/60' : ''}`}
      >
        <KpiCard
          label="Custo dos adiantamentos"
          help="Custo financeiro do capital adiantado a fornecedores antes da emissão da nota ao cliente. Calculado sobre o valor adiantado × dias entre pagamento do adiantamento e emissão da nota × taxa SELIC diária."
        >
          <div className="text-xl lg:text-2xl font-bold font-display text-red-400">
            <SensitiveValue mask="R$ ••••••">{formatCurrencyBR(custosOcultos.custoAdiantamentos)}</SensitiveValue>
          </div>
          <p className="text-brand-gray/60 text-xs mt-2">
            {custosOcultos.qtdAdiantamentos} notas · média {formatDias(custosOcultos.adiantamentoMedioDias)}
          </p>
        </KpiCard>
      </div>

      <div
        onClick={() => onCicloFilterChange(cicloFilter === 'atrasos' ? null : 'atrasos')}
        className={`cursor-pointer transition-all [&>.metric-card]:transition-all ${cicloFilter === 'atrasos' ? '[&>.metric-card]:border-red-400/60' : ''}`}
      >
        <KpiCard
          label="Custo dos atrasos"
          help="Custo financeiro acumulado apenas nos dias de atraso (após o vencimento). Calculado sobre o valor líquido × dias em atraso × taxa SELIC diária."
        >
          <div className="text-xl lg:text-2xl font-bold font-display text-red-400">
            <SensitiveValue mask="R$ ••••••">{formatCurrencyBR(custosOcultos.custoAtrasos)}</SensitiveValue>
          </div>
          <p className="text-brand-gray/60 text-xs mt-2">
            {custosOcultos.qtdAtrasos} notas · atraso médio {formatDias(custosOcultos.atrasoMedioDias)}
          </p>
        </KpiCard>
      </div>

      <KpiCard
        label="Custo oculto total"
        help="Soma dos custos financeiros: ciclo completo + atrasos + adiantamentos. Representa o custo invisível do capital parado ou antecipado nas operações."
      >
        <div className="text-xl lg:text-2xl font-bold font-display text-red-400">
          <SensitiveValue mask="R$ ••••••">{formatCurrencyBR(custosOcultos.custoTotal)}</SensitiveValue>
        </div>
        <p className="text-brand-gray/60 text-xs mt-2">
          Ciclo + Adiantamentos + Atrasos
        </p>
      </KpiCard>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Recebimentos Cards (Previsão + Total em atraso + Custo Atrasos + Maiores Devedores)
// ---------------------------------------------------------------------------

function RecebimentosCards({ notas }: { notas: NotaFiscal[] }) {
  const selic = useSelicRate()
  const [previsaoWindow, setPrevisaoWindow] = useState<PrevisaoWindow>(30)

  const metrics = useMemo(() => {
    const hoje = new Date()
    hoje.setHours(0, 0, 0, 0)
    const hojeIso = hoje.toISOString().split('T')[0]
    const dailyRatePct = selic.daily / 100

    // Previsão de recebimento: notas em aberto dentro da janela
    const limite = new Date(hoje)
    limite.setDate(limite.getDate() + previsaoWindow)
    const previsao = notas
      .filter((n) => n.status === 'aberto')
      .filter((n) => {
        const [y, m, d] = n.dataVencimento.split('-').map(Number)
        const venc = new Date(y, m - 1, d)
        return venc >= hoje && venc <= limite
      })

    // Total em atraso: notas vencidas
    const vencidas = notas.filter((n) => n.status === 'vencido')
    const totalAtraso = vencidas.reduce((s, n) => s + n.valorBruto, 0)
    const diasAtrasoList = vencidas.map((n) => calcDiasAtraso(n.dataVencimento))
    const atrasoMedio = diasAtrasoList.length > 0
      ? diasAtrasoList.reduce((a, b) => a + b, 0) / diasAtrasoList.length
      : 0
    const atrasoMax = diasAtrasoList.length > 0 ? Math.max(...diasAtrasoList) : 0

    // Custo dos atrasos: acumulado em cima das notas em atraso (vencidas ou pagas com atraso)
    let custoAtrasos = 0
    let somaDiasAtraso = 0
    let qtdComAtraso = 0
    for (const nota of notas) {
      if (nota.status === 'cancelada') continue
      const fimAtrasoDate = nota.dataBaixa ?? hojeIso
      const diasAtrasoNota = Math.max(0, daysBetween(nota.dataVencimento, fimAtrasoDate))
      if (diasAtrasoNota > 0) {
        custoAtrasos += nota.valorLiquido * diasAtrasoNota * dailyRatePct
        somaDiasAtraso += diasAtrasoNota
        qtdComAtraso += 1
      }
    }
    const tempoMedioAtraso = qtdComAtraso > 0 ? somaDiasAtraso / qtdComAtraso : 0

    // Inadimplência
    const totalBase = notas.filter((n) => n.status !== 'cancelada').reduce((s, n) => s + n.valorBruto, 0)
    const inadimplenciaPct = totalBase > 0 ? (totalAtraso / totalBase) * 100 : 0

    return {
      previsaoTotal: previsao.reduce((s, n) => s + n.valorBruto, 0),
      previsaoCount: previsao.length,
      totalAtraso,
      vencidasCount: vencidas.length,
      atrasoMedio,
      atrasoMax,
      custoAtrasos,
      tempoMedioAtraso,
      qtdComAtraso,
      inadimplenciaPct,
    }
  }, [notas, selic.daily, previsaoWindow])

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Previsão de recebimento"
          help="Soma do valor bruto das notas em aberto (não vencidas) com vencimento dentro da janela selecionada. Use os botões 7d/15d/30d/60d para ajustar."
        >
          <div className="text-xl lg:text-2xl font-bold font-display text-white">
            <SensitiveValue mask="R$ ••••••">{formatCurrencyBR(metrics.previsaoTotal)}</SensitiveValue>
          </div>
          <div className="mt-2 flex items-center gap-1">
            {PREVISAO_WINDOWS.map((w) => (
              <button
                key={w}
                onClick={() => setPrevisaoWindow(w)}
                className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors cursor-pointer ${
                  previsaoWindow === w
                    ? 'bg-brand-primary/20 text-brand-primary'
                    : 'bg-brand-dark-lighter text-brand-gray hover:text-brand-gray-light'
                }`}
              >
                {w}d
              </button>
            ))}
            <span className="ml-auto text-brand-gray/60 text-xs">{metrics.previsaoCount} notas</span>
          </div>
        </KpiCard>

        <KpiCard
          label="Total em atraso"
          help="Soma do valor bruto de todas as notas vencidas (sem baixa após o vencimento). Indicador direto da carteira inadimplente."
        >
          <div className="text-xl lg:text-2xl font-bold font-display text-red-400">
            <SensitiveValue mask="R$ ••••••">{formatCurrencyBR(metrics.totalAtraso)}</SensitiveValue>
          </div>
          <p className="text-brand-gray/60 text-xs mt-2">
            {metrics.vencidasCount} {metrics.vencidasCount === 1 ? 'nota vencida' : 'notas vencidas'}
            {metrics.vencidasCount > 0 && ` · atraso médio ${formatDias(metrics.atrasoMedio)} · máx ${formatDias(metrics.atrasoMax)}`}
          </p>
        </KpiCard>

        <KpiCard
          label="Custo dos atrasos"
          help="Custo financeiro acumulado a partir do 1º dia de atraso. Calculado sobre o valor líquido multiplicado pelos dias de atraso e pela taxa SELIC diária."
        >
          <div className="text-xl lg:text-2xl font-bold font-display text-red-400">
            <SensitiveValue mask="R$ ••••••">{formatCurrencyBR(metrics.custoAtrasos)}</SensitiveValue>
          </div>
          <p className="text-brand-gray/60 text-xs mt-2">
            Atraso médio: {formatDias(metrics.tempoMedioAtraso)} ({metrics.qtdComAtraso} notas)
          </p>
        </KpiCard>

        <KpiCard
          label="Taxa de inadimplência"
          help="Percentual do valor vencido sobre o total emitido (excluindo canceladas). Indicador direto do risco de não-pagamento."
        >
          <div className={`text-xl lg:text-2xl font-bold font-display ${metrics.inadimplenciaPct > 5 ? 'text-red-400' : 'text-white'}`}>
            {metrics.inadimplenciaPct.toFixed(1)}%
          </div>
          <p className="text-brand-gray/60 text-xs mt-2">
            <SensitiveValue mask="R$ ••••••">{formatCompact(metrics.totalAtraso)}</SensitiveValue> em notas vencidas
          </p>
        </KpiCard>
      </div>

      <MaioresDevedoresCard notas={notas} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared hook: contract consumption data
// ---------------------------------------------------------------------------

function useContratosConsumo(clienteFilter: Set<string>, periodStart: string, periodEnd: string) {
  const contratosAtivos = useMemo(
    () => {
      let data = MOCK_CONTRATOS.filter((c) => c.status === 'ativo' || c.status === 'em-negociacao')
      if (clienteFilter.size > 0) data = data.filter((c) => clienteFilter.has(c.cliente))
      return data
    },
    [clienteFilter],
  )

  const contratosComConsumo = useMemo(() => {
    const todayISO = new Date().toISOString().split('T')[0]

    return contratosAtivos
      .map((contrato) => {
        const notasContrato = MOCK_NOTAS.filter(
          (n) => n.contrato === contrato.numero && n.status !== 'cancelada',
        )

        const valorLimiteTotal = contrato.valorLimite + contrato.aditivos.reduce((s, a) => s + (a.valorAdicional ?? 0), 0)
        const valorLimite = valorLimiteTotal / (contrato.agenciasEnvolvidas || 1)

        const notasNoPeriodo = notasContrato.filter(
          (n) => n.dataEmissao >= periodStart && n.dataEmissao <= periodEnd,
        )
        const notasForaPeriodo = notasContrato.filter(
          (n) => n.dataEmissao < periodStart || n.dataEmissao > periodEnd,
        )
        const valorPeriodo = notasNoPeriodo.reduce((s, n) => s + n.valorBruto, 0)
        const valorFora = notasForaPeriodo.reduce((s, n) => s + n.valorBruto, 0)
        const valorEmitido = valorPeriodo + valorFora

        const pctFora = valorLimite > 0 ? (valorFora / valorLimite) * 100 : 0
        const pctPeriodo = valorLimite > 0 ? (valorPeriodo / valorLimite) * 100 : 0
        const percentualUsado = valorLimite > 0 ? (valorEmitido / valorLimite) * 100 : 0
        const valorRestante = Math.max(0, valorLimite - valorEmitido)

        const diffMs = new Date(contrato.periodo.fim).getTime() - new Date(todayISO).getTime()
        const mesesRestantes = Math.max(1, diffMs / (1000 * 60 * 60 * 24 * 30))
        const diffMsDecorridos = new Date(todayISO).getTime() - new Date(contrato.periodo.inicio).getTime()
        const mesesDecorridos = Math.max(1, diffMsDecorridos / (1000 * 60 * 60 * 24 * 30))
        const projecaoLinear = valorRestante / mesesRestantes
        const ritmoMensal = valorEmitido / mesesDecorridos
        const risco = ritmoMensal > projecaoLinear
        const projecaoFinal = ritmoMensal * (mesesDecorridos + mesesRestantes)
        const percentualProjetado = valorLimite > 0 ? (projecaoFinal / valorLimite) * 100 : 0

        return {
          contrato,
          valorLimite,
          valorEmitido,
          valorPeriodo,
          valorFora,
          pctFora,
          pctPeriodo,
          percentualUsado,
          valorRestante,
          mesesRestantes,
          projecaoLinear,
          ritmoMensal,
          risco,
          percentualProjetado,
          notasCount: notasContrato.length,
        }
      })
      .sort((a, b) => b.percentualUsado - a.percentualUsado)
  }, [contratosAtivos, periodStart, periodEnd])

  const totals = useMemo(() => {
    const totalLimite = contratosComConsumo.reduce((s, c) => s + c.valorLimite, 0)
    const totalPeriodo = contratosComConsumo.reduce((s, c) => s + c.valorPeriodo, 0)
    const totalFora = contratosComConsumo.reduce((s, c) => s + c.valorFora, 0)
    const totalEmitido = totalPeriodo + totalFora
    const totalRestante = Math.max(0, totalLimite - totalEmitido)
    const pctUsado = totalLimite > 0 ? (totalEmitido / totalLimite) * 100 : 0
    const pctPeriodo = totalLimite > 0 ? (totalPeriodo / totalLimite) * 100 : 0
    const pctFora = totalLimite > 0 ? (totalFora / totalLimite) * 100 : 0
    return { totalLimite, totalPeriodo, totalFora, totalEmitido, totalRestante, pctUsado, pctPeriodo, pctFora }
  }, [contratosComConsumo])

  return { contratosAtivos, contratosComConsumo, totals }
}

// ---------------------------------------------------------------------------
// Circular consumption chart (reused across tabs)
// ---------------------------------------------------------------------------

function ConsumoContratualChart({ totals }: {
  totals: { totalLimite: number; totalPeriodo: number; totalFora: number; totalEmitido: number; totalRestante: number; pctUsado: number; pctPeriodo: number; pctFora: number }
}) {
  const pctDisponivel = Math.max(0, 100 - totals.pctFora - totals.pctPeriodo)

  return (
    <div className="metric-card">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-brand-gray uppercase tracking-wide">Consumo contratual</span>
        <span className={`text-xl font-bold font-display ${totals.pctUsado > 95 ? 'text-red-400' : totals.pctUsado > 80 ? 'text-yellow-400' : 'text-white'}`}>
          {totals.pctUsado.toFixed(1)}%
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full h-[5px] flex items-center gap-[3px] mb-4">
        {totals.pctFora > 0 && (
          <div className="h-[5px] rounded-full bg-brand-gray/40 transition-all duration-500" style={{ width: `${totals.pctFora}%` }} />
        )}
        {totals.pctPeriodo > 0 && (
          <div className="h-[5px] rounded-full bg-brand-primary transition-all duration-500" style={{ width: `${totals.pctPeriodo}%` }} />
        )}
        {pctDisponivel > 0 && (
          <div className="h-[5px] rounded-full bg-white flex-1 transition-all duration-500" />
        )}
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <span className="text-[10px] text-brand-gray/60 block">Fatia estimada contratual</span>
          <span className="text-sm font-semibold text-white"><SensitiveValue mask="R$ ••••">{formatCompact(totals.totalLimite)}</SensitiveValue></span>
        </div>
        <div>
          <span className="text-[10px] text-brand-primary block flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-brand-primary inline-block" /> No período</span>
          <span className="text-sm font-semibold text-brand-primary"><SensitiveValue mask="R$ ••••">{formatCompact(totals.totalPeriodo)}</SensitiveValue></span>
        </div>
        <div>
          <span className="text-[10px] text-brand-gray/60 block flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-brand-gray/40 inline-block" /> Em outros períodos</span>
          <span className="text-sm font-semibold text-brand-gray"><SensitiveValue mask="R$ ••••">{formatCompact(totals.totalFora)}</SensitiveValue></span>
        </div>
        <div>
          <span className="text-[10px] text-brand-gray/60 block flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-white inline-block" /> Disponível</span>
          <span className="text-sm font-semibold text-white"><SensitiveValue mask="R$ ••••">{formatCompact(totals.totalRestante)}</SensitiveValue></span>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Execução Contratual Cards
// ---------------------------------------------------------------------------

function ExecucaoContratualCards({ clienteFilter, periodStart, periodEnd }: {
  clienteFilter: Set<string>
  periodStart: string
  periodEnd: string
}) {
  const { contratosComConsumo, totals } = useContratosConsumo(clienteFilter, periodStart, periodEnd)

  const formatMonth = (iso: string) => {
    const [y, m] = iso.split('-')
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
    return `${months[parseInt(m, 10) - 1]}/${y.slice(2)}`
  }

  if (contratosComConsumo.length === 0) {
    return (
      <div className="text-center py-10">
        <p className="text-brand-gray/60 text-sm">Nenhum contrato ativo encontrado</p>
      </div>
    )
  }

  const singleClient = contratosComConsumo.length === 1

  return (
    <div className="space-y-4">
      {!singleClient && <ConsumoContratualChart totals={totals} />}

      {/* Contract cards */}
      <div className={singleClient ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4'}>
        {contratosComConsumo.map(({ contrato, valorLimite, valorEmitido, valorPeriodo, valorFora, pctFora, pctPeriodo, percentualUsado, valorRestante, mesesRestantes, projecaoLinear, ritmoMensal, risco, percentualProjetado }) => (
          <div key={contrato.id} className="metric-card">
            {/* Header: cliente à esquerda, contrato + prazo à direita */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-bold text-white truncate min-w-0">{contrato.cliente}</span>
              <span className="text-xs text-brand-gray/50 shrink-0">
                Contrato {contrato.numero} · {formatMonth(contrato.periodo.inicio)} → {formatMonth(contrato.periodo.fim)}
              </span>
            </div>

            {/* Value + percentage */}
            <div className="mb-1.5">
              <span className="text-xs text-brand-gray block mb-0.5">Consumido do estimado contratual</span>
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-semibold text-white">
                  <SensitiveValue mask="R$ ••••">{formatCompact(valorEmitido)}</SensitiveValue>
                  <span className="text-xs font-normal text-brand-gray/60"> / {formatCompact(valorLimite)}</span>
                </span>
                <span className={`text-xs font-semibold ${percentualUsado > 95 ? 'text-red-400' : percentualUsado > 80 ? 'text-yellow-400' : 'text-brand-primary'}`}>
                  {percentualUsado.toFixed(1)}%
                </span>
              </div>
            </div>

            {/* Progress bar — Big Numbers style (h-[3px]) with 3 segments */}
            <div className="pt-0.5 mb-3">
              <div className="w-full h-[3px] flex items-center gap-[3px]">
                {pctFora > 0 && (
                  <div
                    className="h-[3px] rounded-full bg-brand-gray/40 transition-all duration-500"
                    style={{ width: `${Math.min(100, pctFora)}%` }}
                    title={`Outros períodos: ${formatCompact(valorFora)}`}
                  />
                )}
                {pctPeriodo > 0 && (
                  <div
                    className="h-[3px] rounded-full bg-brand-primary transition-all duration-500"
                    style={{ width: `${Math.min(100 - pctFora, pctPeriodo)}%` }}
                    title={`Período atual: ${formatCompact(valorPeriodo)}`}
                  />
                )}
                <div
                  className="h-[3px] rounded-full bg-white flex-1 transition-all duration-500"
                  title={`Disponível: ${formatCompact(valorRestante)}`}
                />
              </div>
            </div>

            {/* Metrics — stacked rows */}
            <div className="flex flex-col gap-1.5 pt-3 border-t border-brand-dark-lighter">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-brand-primary">No período</span>
                <span className="text-sm font-semibold text-brand-primary">
                  <SensitiveValue mask="R$ ••••">{formatCompact(valorPeriodo)}</SensitiveValue>
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-brand-gray">Em outros períodos</span>
                <span className="text-sm font-semibold text-brand-gray">
                  <SensitiveValue mask="R$ ••••">{formatCompact(valorFora)}</SensitiveValue>
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-brand-gray">Disponível (da fatia Monumenta)</span>
                <span className="text-sm font-semibold text-white">
                  <SensitiveValue mask="R$ ••••">{formatCompact(valorRestante)}</SensitiveValue>
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-brand-gray">Ritmo no período (por mês)</span>
                <span className={`text-sm font-semibold ${risco ? 'text-red-400' : 'text-white'}`}>
                  <SensitiveValue mask="R$ ••••">{formatCompact(ritmoMensal)}</SensitiveValue>
                  <span className="text-brand-gray/40 font-normal text-[10px]">/m</span>
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-brand-gray">Projeção - Final do contrato (nesse ritmo)</span>
                <span className={`text-sm font-semibold ${percentualProjetado > 100 ? 'text-red-400' : 'text-brand-primary'}`}>
                  {percentualProjetado.toFixed(0)}%
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Top 10 Serviços Card (com filtro NF/ND)
// ---------------------------------------------------------------------------

const TIPO_FILTER_OPTIONS = ['Todas', 'NF', 'ND'] as const
type TipoFilterOption = (typeof TIPO_FILTER_OPTIONS)[number]

function TopServicosCard({ notas }: { notas: NotaFiscal[] }) {
  const [tipoFilter, setTipoFilter] = useState<TipoFilterOption>('Todas')

  const topServicos = useMemo(() => {
    const base = (tipoFilter === 'Todas' ? notas : notas.filter((n) => n.tipo === tipoFilter))
      .filter((n) => n.status !== 'cancelada')
    const totalValor = base.reduce((s, n) => s + n.valorBruto, 0)

    const svcMap = new Map<string, { valor: number; qtd: number }>()
    for (const n of base) {
      const entry = svcMap.get(n.tipoServico) ?? { valor: 0, qtd: 0 }
      entry.valor += n.valorBruto
      entry.qtd += 1
      svcMap.set(n.tipoServico, entry)
    }

    return Array.from(svcMap.entries())
      .map(([nome, { valor, qtd }]) => ({
        nome,
        valor,
        qtd,
        pct: totalValor > 0 ? (valor / totalValor) * 100 : 0,
      }))
      .sort((a, b) => b.valor - a.valor)
      .slice(0, 10)
  }, [notas, tipoFilter])

  return (
    <KpiCard
      label="Top 10 — Serviços"
      help="Os 10 tipos de serviço com maior valor faturado no período. Mostra percentual sobre o total, quantidade de notas e valor somado."
      headerRight={
        <div className="flex items-center gap-1">
          {TIPO_FILTER_OPTIONS.map((opt) => (
            <button
              key={opt}
              onClick={() => setTipoFilter(opt)}
              className={`px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wide transition-colors cursor-pointer ${
                tipoFilter === opt
                  ? 'bg-brand-primary/20 text-brand-primary'
                  : 'text-brand-gray/60 hover:text-brand-gray hover:bg-brand-dark-lighter'
              }`}
            >
              {opt === 'Todas' ? 'Todas as notas' : opt}
            </button>
          ))}
        </div>
      }
    >
      {topServicos.length > 0 ? (
        <div className="space-y-2">
          {topServicos.map((svc, i) => (
            <div key={svc.nome} className="flex items-center gap-3">
              <span className="text-brand-gray/50 text-[10px] font-mono w-4 text-right shrink-0">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="text-xs text-white truncate">{svc.nome}</span>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-[10px] text-brand-gray/60">{svc.qtd} {svc.qtd === 1 ? 'nota' : 'notas'}</span>
                    <span className="text-xs font-semibold text-white">
                      <SensitiveValue mask="R$ ••••">{formatCompact(svc.valor)}</SensitiveValue>
                    </span>
                    <span className="text-[10px] text-brand-primary font-semibold w-10 text-right">{svc.pct.toFixed(1)}%</span>
                  </div>
                </div>
                <div className="h-1 rounded-full bg-brand-dark-lighter overflow-hidden">
                  <div className="h-full rounded-full bg-brand-primary/60" style={{ width: `${svc.pct}%` }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-brand-gray/60 text-xs">Sem dados</p>
      )}
    </KpiCard>
  )
}

// ---------------------------------------------------------------------------
// Composição Cards
// ---------------------------------------------------------------------------

function ComposicaoCards({ notas }: { notas: NotaFiscal[] }) {
  const metrics = useMemo(() => {
    const base = notas.filter((n) => n.status !== 'cancelada')

    // Tipo de Serviço (servicoTipo)
    const svcDigital = base.filter((n) => n.servicoTipo === 'digital').reduce((s, n) => s + n.valorBruto, 0)
    const svcPromo = base.filter((n) => n.servicoTipo === 'promo').reduce((s, n) => s + n.valorBruto, 0)
    const svcTotal = svcDigital + svcPromo
    const svcDigitalPct = svcTotal > 0 ? (svcDigital / svcTotal) * 100 : 0
    const svcPromoPct = svcTotal > 0 ? (svcPromo / svcTotal) * 100 : 0

    // Tipo de Contrato (digitalPromo)
    const ctrDigital = base.filter((n) => n.digitalPromo === 'digital').reduce((s, n) => s + n.valorBruto, 0)
    const ctrPromo = base.filter((n) => n.digitalPromo === 'promo').reduce((s, n) => s + n.valorBruto, 0)
    const ctrTotal = ctrDigital + ctrPromo
    const ctrDigitalPct = ctrTotal > 0 ? (ctrDigital / ctrTotal) * 100 : 0
    const ctrPromoPct = ctrTotal > 0 ? (ctrPromo / ctrTotal) * 100 : 0

    // Retenção média: (bruto - liquido) / bruto (apenas notas não canceladas)
    let somaPct = 0
    let qtd = 0
    for (const n of base) {
      if (n.valorBruto > 0) {
        somaPct += (n.valorBruto - n.valorLiquido) / n.valorBruto
        qtd += 1
      }
    }
    const retencaoMediaPct = qtd > 0 ? (somaPct / qtd) * 100 : 0

    return { svcDigital, svcPromo, svcDigitalPct, svcPromoPct, ctrDigital, ctrPromo, ctrDigitalPct, ctrPromoPct, retencaoMediaPct }
  }, [notas])

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <KpiCard
        label="Tipo de Serviço"
        help="Divisão do valor bruto entre serviços Digital e Promo, classificados pelo tipo de serviço da nota."
      >
        <div className="text-xl lg:text-2xl font-bold font-display text-white">
          {metrics.svcDigitalPct.toFixed(0)}% / {metrics.svcPromoPct.toFixed(0)}%
        </div>
        <div className="mt-2">
          <div className="h-1.5 flex rounded-full overflow-hidden bg-brand-dark-lighter">
            <div className="bg-brand-primary" style={{ width: `${metrics.svcDigitalPct}%` }} />
            <div className="bg-brand-gray/60" style={{ width: `${metrics.svcPromoPct}%` }} />
          </div>
          <div className="flex justify-between text-brand-gray/60 text-[10px] mt-1.5">
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-brand-primary" /> Digital · <SensitiveValue mask="R$ ••••">{formatCurrencyBR(metrics.svcDigital)}</SensitiveValue></span>
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-brand-gray/60" /> Promo · <SensitiveValue mask="R$ ••••">{formatCurrencyBR(metrics.svcPromo)}</SensitiveValue></span>
          </div>
        </div>
      </KpiCard>

      <KpiCard
        label="Tipo de Contrato"
        help="Divisão do valor bruto entre contratos Digital e Promo, classificados pelo tipo de contrato (derivado do PIT)."
      >
        <div className="text-xl lg:text-2xl font-bold font-display text-white">
          {metrics.ctrDigitalPct.toFixed(0)}% / {metrics.ctrPromoPct.toFixed(0)}%
        </div>
        <div className="mt-2">
          <div className="h-1.5 flex rounded-full overflow-hidden bg-brand-dark-lighter">
            <div className="bg-brand-primary" style={{ width: `${metrics.ctrDigitalPct}%` }} />
            <div className="bg-brand-gray/60" style={{ width: `${metrics.ctrPromoPct}%` }} />
          </div>
          <div className="flex justify-between text-brand-gray/60 text-[10px] mt-1.5">
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-brand-primary" /> Digital · <SensitiveValue mask="R$ ••••">{formatCurrencyBR(metrics.ctrDigital)}</SensitiveValue></span>
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-brand-gray/60" /> Promo · <SensitiveValue mask="R$ ••••">{formatCurrencyBR(metrics.ctrPromo)}</SensitiveValue></span>
          </div>
        </div>
      </KpiCard>

      <KpiCard
        label="Retenção média consolidada"
        help="Média de impostos retidos (PIS/COFINS/ISS/IR) sobre o valor bruto — (bruto - líquido) / bruto."
      >
        <div className="text-xl lg:text-2xl font-bold font-display text-white">
          {metrics.retencaoMediaPct.toFixed(1)}%
        </div>
        <p className="text-brand-gray/60 text-xs mt-2">
          Média das notas no período
        </p>
      </KpiCard>

      <div className="lg:col-span-3 md:col-span-2 grid grid-cols-1 lg:grid-cols-5 gap-4 items-stretch">
        <div className="lg:col-span-2 [&>.metric-card]:h-full">
          <CategorizacaoCard notas={notas} />
        </div>
        <div className="lg:col-span-3 [&>.metric-card]:h-full">
          <TopServicosCard notas={notas} />
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Categorização de Serviços
// ---------------------------------------------------------------------------

const CATEGORIA_SERVICO: Record<string, string> = {
  'Planejamento': 'Essenciais',
  'Consultoria': 'Essenciais',
  'Design Digital': 'Complementares',
  'Produção Gráfica': 'Complementares',
  'Projeto Executivo': 'Complementares',
  'Identidade Individual': 'Complementares',
  'Honorários': 'Honorários',
  'Hospedagem': 'Reembolsos',
}

const CATEGORIAS_ORDER = ['Essenciais', 'Complementares', 'Honorários', 'Reembolsos', 'Outros'] as const

const CATEGORIA_COLORS: Record<string, string> = {
  Essenciais: 'bg-brand-primary',
  Complementares: 'bg-brand-primary/40',
  Honorários: 'bg-zinc-500',
  Reembolsos: 'bg-zinc-400',
  Outros: 'bg-zinc-600',
}

function CategorizacaoCard({ notas }: { notas: NotaFiscal[] }) {
  const categorias = useMemo(() => {
    const base = notas.filter((n) => n.status !== 'cancelada')
    const totalValor = base.reduce((s, n) => s + n.valorBruto, 0)

    const catMap = new Map<string, { valor: number; qtd: number }>()
    for (const cat of CATEGORIAS_ORDER) catMap.set(cat, { valor: 0, qtd: 0 })

    for (const n of base) {
      const cat = CATEGORIA_SERVICO[n.tipoServico] ?? 'Outros'
      const entry = catMap.get(cat)!
      entry.valor += n.valorBruto
      entry.qtd += 1
    }

    const cats = CATEGORIAS_ORDER.map((cat) => {
      const { valor, qtd } = catMap.get(cat)!
      return { cat, valor, qtd, pct: totalValor > 0 ? (valor / totalValor) * 100 : 0 }
    })
    return { cats, totalValor }
  }, [notas])

  return (
    <KpiCard
      label="Categorização"
      help="Divisão dos serviços faturados por categoria: Essenciais (Honorários, Planejamento, Consultoria), Complementares (Design, Produção, Projeto), Reembolsos (Hospedagem) e Outros."
    >
      <div className="flex flex-col h-full">
        <div className="flex items-baseline justify-between mb-3">
          <span className="text-xs text-brand-gray/60">Total emitido</span>
          <span className="text-xl lg:text-2xl font-bold font-display text-white">
            <SensitiveValue mask="R$ ••••••">{formatCompact(categorias.totalValor)}</SensitiveValue>
          </span>
        </div>
        {/* Stacked bar */}
        <div className="h-1.5 flex rounded-full overflow-hidden bg-brand-dark-lighter">
          {categorias.cats.map((c) => (
            c.pct > 0 ? <div key={c.cat} className={`${CATEGORIA_COLORS[c.cat]}`} style={{ width: `${c.pct}%` }} /> : null
          ))}
        </div>
        {/* Detail rows */}
        <div className="flex flex-col flex-1 mt-4 divide-y divide-white/10">
          {categorias.cats.map((c) => (
            <div key={c.cat} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${CATEGORIA_COLORS[c.cat]}`} />
              <span className="text-xs text-white truncate flex-1 min-w-0">{c.cat}</span>
              <span className="text-[10px] text-brand-gray/60 shrink-0">{c.qtd} {c.qtd === 1 ? 'nota' : 'notas'}</span>
              <span className="text-xs font-semibold text-white w-16 text-right shrink-0">
                <SensitiveValue mask="R$ ••••">{formatCompact(c.valor)}</SensitiveValue>
              </span>
              <span className="text-[10px] text-brand-primary font-semibold w-10 text-right shrink-0">{c.pct.toFixed(1)}%</span>
            </div>
          ))}
        </div>
      </div>
    </KpiCard>
  )
}

// ---------------------------------------------------------------------------
// Risco Cards
// ---------------------------------------------------------------------------

function RiscoCards({ notas }: { notas: NotaFiscal[] }) {
  const metrics = useMemo(() => {
    const base = notas.filter((n) => n.status !== 'cancelada')
    const totalBase = base.reduce((s, n) => s + n.valorBruto, 0)

    const vencidoValor = base.filter((n) => n.status === 'vencido').reduce((s, n) => s + n.valorBruto, 0)
    const inadimplenciaPct = totalBase > 0 ? (vencidoValor / totalBase) * 100 : 0

    // Concentração: top 5 clientes por valor em aberto + vencido
    const abertoVencido = base.filter((n) => n.status === 'aberto' || n.status === 'vencido')
    const totalAberto = abertoVencido.reduce((s, n) => s + n.valorBruto, 0)
    const porCliente = new Map<string, number>()
    for (const n of abertoVencido) {
      porCliente.set(n.cliente, (porCliente.get(n.cliente) ?? 0) + n.valorBruto)
    }
    const rankingClientes = Array.from(porCliente.entries())
      .map(([cliente, valor]) => ({ cliente, valor, pct: totalAberto > 0 ? (valor / totalAberto) * 100 : 0 }))
      .sort((a, b) => b.valor - a.valor)
      .slice(0, 5)
    const top1Pct = rankingClientes[0]?.pct ?? 0

    return { inadimplenciaPct, vencidoValor, top1Pct, rankingClientes }
  }, [notas])

  const stackColors = [
    'bg-brand-primary',
    'bg-brand-primary/70',
    'bg-brand-primary/45',
    'bg-brand-primary/25',
    'bg-brand-primary/10',
  ]

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <KpiCard
        label="Taxa de inadimplência"
        help="Percentual do valor vencido sobre o total emitido (excluindo canceladas). Indicador direto do risco de não-pagamento."
      >
        <div className="text-xl lg:text-2xl font-bold font-display text-white">
          {metrics.inadimplenciaPct.toFixed(1)}%
        </div>
        <p className="text-brand-gray/60 text-xs mt-2">
          <SensitiveValue mask="R$ ••••••">{formatCompact(metrics.vencidoValor)}</SensitiveValue> em notas vencidas
        </p>
      </KpiCard>

      <KpiCard
        label="Concentração de recebíveis"
        help="Percentual do maior cliente sobre o total em aberto + vencido. Barra mostra os top 5 clientes empilhados em variações de verde — do mais ao menos representativo."
      >
        <div className="text-xl lg:text-2xl font-bold font-display text-white">
          {metrics.top1Pct.toFixed(1)}%
        </div>
        <div className="mt-2">
          {metrics.rankingClientes.length > 0 ? (
            <>
              <div className="h-1.5 flex rounded-full overflow-hidden bg-brand-dark-lighter">
                {metrics.rankingClientes.map((c, i) => (
                  <div
                    key={c.cliente}
                    className={stackColors[i]}
                    style={{ width: `${c.pct}%` }}
                    title={`${c.cliente}: ${c.pct.toFixed(1)}%`}
                  />
                ))}
              </div>
              <p className="text-brand-gray/60 text-xs mt-2 truncate">
                Top 1: {metrics.rankingClientes[0].cliente}
              </p>
            </>
          ) : (
            <p className="text-brand-gray/60 text-xs">Sem dados</p>
          )}
        </div>
      </KpiCard>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Maiores Devedores
// ---------------------------------------------------------------------------

function MaioresDevedoresCard({ notas }: { notas: NotaFiscal[] }) {
  const devedores = useMemo(() => {
    const abertos = notas.filter((n) => n.status === 'aberto' || n.status === 'vencido')
    const porCliente = new Map<string, { valor: number; vencido: number; diasAtrasoSum: number; qtdAtraso: number; qtd: number }>()

    for (const nota of abertos) {
      const entry = porCliente.get(nota.cliente) ?? { valor: 0, vencido: 0, diasAtrasoSum: 0, qtdAtraso: 0, qtd: 0 }
      entry.valor += nota.valorBruto
      entry.qtd += 1
      if (nota.status === 'vencido') {
        entry.vencido += nota.valorBruto
        const atraso = calcDiasAtraso(nota.dataVencimento)
        if (atraso > 0) {
          entry.diasAtrasoSum += atraso
          entry.qtdAtraso += 1
        }
      }
      porCliente.set(nota.cliente, entry)
    }

    return Array.from(porCliente.entries())
      .map(([cliente, data]) => ({
        cliente,
        valor: data.valor,
        vencido: data.vencido,
        qtd: data.qtd,
        diasMedios: data.qtdAtraso > 0 ? Math.round(data.diasAtrasoSum / data.qtdAtraso) : 0,
      }))
      .sort((a, b) => b.valor - a.valor)
      .slice(0, 5)
  }, [notas])

  const devedoresHelpContent = 'Top 5 clientes com maior volume em aberto (soma de notas em aberto + vencidas).\n\n• Em aberto: valor bruto total com o cliente\n• Vencida: parcela do valor que já passou do vencimento\n• Atraso médio: média de dias de atraso entre as notas vencidas do cliente'

  if (devedores.length === 0) {
    return (
      <div className="metric-card relative">
        <div className="absolute top-3 right-3">
          <KpiHelp title="Maiores devedores" content={devedoresHelpContent} />
        </div>
        <div className="flex items-center pr-6">
          <span className="text-xs font-medium text-brand-gray uppercase tracking-wide">Maiores devedores</span>
        </div>
        <p className="text-brand-gray/60 text-sm mt-4">Sem notas em aberto no período.</p>
      </div>
    )
  }

  const maxValor = devedores[0]?.valor ?? 1

  return (
    <div className="metric-card relative">
      <div className="absolute top-3 right-3">
        <KpiHelp title="Maiores devedores" content={devedoresHelpContent} />
      </div>
      <div className="flex items-center pr-6">
        <span className="text-xs font-medium text-brand-gray uppercase tracking-wide">Maiores devedores</span>
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-brand-gray text-xs uppercase tracking-wide border-b border-brand-dark-lighter">
              <th className="text-left font-medium py-2 pr-4">#</th>
              <th className="text-left font-medium py-2 pr-4">Cliente</th>
              <th className="text-right font-medium py-2 pr-4">Em aberto</th>
              <th className="text-right font-medium py-2 pr-4">Vencida</th>
              <th className="text-right font-medium py-2 pr-4">Notas</th>
              <th className="text-right font-medium py-2 pr-4">Atraso médio</th>
              <th className="text-left font-medium py-2 min-w-[120px]">Share</th>
            </tr>
          </thead>
          <tbody>
            {devedores.map((d, i) => (
              <tr key={d.cliente} className="border-b border-brand-dark-lighter/60 last:border-b-0">
                <td className="py-2.5 pr-4 text-brand-gray">{i + 1}</td>
                <td className="py-2.5 pr-4 text-white font-medium">{d.cliente}</td>
                <td className="py-2.5 pr-4 text-right text-white tabular-nums">
                  <SensitiveValue mask="R$ ••••">{formatCompact(d.valor)}</SensitiveValue>
                </td>
                <td className="py-2.5 pr-4 text-right tabular-nums">
                  {d.vencido > 0 ? (
                    <span className="text-red-400">
                      <SensitiveValue mask="R$ ••••">{formatCompact(d.vencido)}</SensitiveValue>
                    </span>
                  ) : (
                    <span className="text-brand-gray/60">—</span>
                  )}
                </td>
                <td className="py-2.5 pr-4 text-right text-brand-gray tabular-nums">{d.qtd}</td>
                <td className="py-2.5 pr-4 text-right tabular-nums">
                  {d.diasMedios > 0 ? (
                    <span className="text-red-400">{d.diasMedios}d</span>
                  ) : (
                    <span className="text-brand-gray/60">—</span>
                  )}
                </td>
                <td className="py-2.5">
                  <div className="h-1.5 w-full bg-brand-dark-lighter rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-primary/80"
                      style={{ width: `${(d.valor / maxValor) * 100}%` }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Multi-select filter dropdown — SmartFilter visual pattern */
function FilterDropdown({ label, options, selected, onToggle, onClear }: {
  label: string
  options: string[]
  selected: Set<string>
  onToggle: (option: string) => void
  onClear: () => void
}) {
  const [open, setOpen] = useState(false)
  const buttonRef = useState<HTMLButtonElement | null>(null)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 })
  const hasSelection = selected.size > 0

  const handleOpen = () => {
    if (!open && buttonRef[0]) {
      const rect = buttonRef[0].getBoundingClientRect()
      setDropdownPos({ top: rect.bottom + 4, left: clampDropdownLeft(rect.left, 280) })
    }
    setOpen(!open)
  }

  return (
    <div className="relative">
      <button
        ref={(el) => { buttonRef[0] = el }}
        onClick={handleOpen}
        className={`
          filter-dropdown-button flex items-center gap-2 py-1.5 px-3 rounded-lg cursor-pointer text-xs font-medium transition-all duration-150
          ${hasSelection
            ? 'border border-brand-primary bg-brand-primary/20 text-brand-primary'
            : 'border border-brand-dark-lighter bg-brand-dark-lighter text-brand-gray hover:bg-brand-dark hover:text-white'
          }
        `}
      >
        {label}
        {hasSelection && (
          <span className="text-[0.7rem] py-0.5 px-1.5 rounded-full bg-brand-primary text-brand-dark">
            {selected.size}
          </span>
        )}
        <span className="text-xs">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="fixed z-50 bg-brand-dark-light border border-brand-dark-lighter rounded-lg shadow-monumenta-lg min-w-[250px] max-h-[300px] overflow-y-auto p-2"
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
          >
            {hasSelection && (
              <div
                onClick={() => { onClear(); setOpen(false) }}
                className="flex items-center gap-2 p-2 rounded cursor-pointer text-red-400 hover:bg-brand-dark-lighter transition-colors duration-150 mb-1"
              >
                <X className="w-3 h-3" />
                <span className="text-sm">Limpar filtro</span>
              </div>
            )}
            {options.map((option) => {
              const isSelected = selected.has(option)
              return (
                <div
                  key={option}
                  onClick={() => onToggle(option)}
                  className={`
                    flex items-center gap-2 p-2 rounded cursor-pointer transition-colors duration-150
                    ${isSelected ? 'bg-brand-primary/20' : 'hover:bg-brand-dark-lighter'}
                  `}
                >
                  <span className={`
                    w-4 h-4 rounded flex items-center justify-center text-[0.7rem]
                    ${isSelected
                      ? 'border-2 border-brand-primary bg-brand-primary text-brand-dark'
                      : 'border-2 border-brand-dark-lighter bg-transparent'
                    }
                  `}>
                    {isSelected && <Check className="w-2.5 h-2.5" />}
                  </span>
                  <span className="text-sm text-white">{option}</span>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

/** FilterDropdown with search input for long lists */
function SearchableFilterDropdown({ label, options, selected, onToggle, onClear }: {
  label: string
  options: string[]
  selected: Set<string>
  onToggle: (option: string) => void
  onClear: () => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const buttonRef = useState<HTMLButtonElement | null>(null)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 })
  const hasSelection = selected.size > 0

  const filtered = useMemo(() => {
    if (!query) return options
    const q = query.toLowerCase()
    return options.filter((o) => o.toLowerCase().includes(q))
  }, [options, query])

  const handleOpen = () => {
    if (!open && buttonRef[0]) {
      const rect = buttonRef[0].getBoundingClientRect()
      setDropdownPos({ top: rect.bottom + 4, left: clampDropdownLeft(rect.left, 300) })
    }
    setOpen(!open)
    if (open) setQuery('')
  }

  return (
    <div className="relative">
      <button
        ref={(el) => { buttonRef[0] = el }}
        onClick={handleOpen}
        className={`
          filter-dropdown-button flex items-center gap-2 py-1.5 px-3 rounded-lg cursor-pointer text-xs font-medium transition-all duration-150
          ${hasSelection
            ? 'border border-brand-primary bg-brand-primary/20 text-brand-primary'
            : 'border border-brand-dark-lighter bg-brand-dark-lighter text-brand-gray hover:bg-brand-dark hover:text-white'
          }
        `}
      >
        {label}
        {hasSelection && (
          <span className="text-[0.7rem] py-0.5 px-1.5 rounded-full bg-brand-primary text-brand-dark">
            {selected.size}
          </span>
        )}
        <span className="text-xs">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setQuery('') }} />
          <div
            className="fixed z-50 bg-brand-dark-light border border-brand-dark-lighter rounded-lg shadow-monumenta-lg min-w-[280px] max-h-[340px] flex flex-col p-2"
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
          >
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar..."
              className="w-full px-2.5 py-1.5 mb-2 rounded bg-brand-dark-lighter border border-brand-dark-lighter text-sm text-white placeholder-brand-gray/50 outline-none focus:border-brand-primary/50"
            />
            {hasSelection && (
              <div
                onClick={() => { onClear(); setOpen(false); setQuery('') }}
                className="flex items-center gap-2 p-2 rounded cursor-pointer text-red-400 hover:bg-brand-dark-lighter transition-colors duration-150 mb-1"
              >
                <X className="w-3 h-3" />
                <span className="text-sm">Limpar filtro</span>
              </div>
            )}
            <div className="overflow-y-auto flex-1">
              {filtered.length === 0 ? (
                <p className="text-sm text-brand-gray/50 p-2">Nenhum resultado</p>
              ) : (
                filtered.map((option) => {
                  const isSelected = selected.has(option)
                  return (
                    <div
                      key={option}
                      onClick={() => onToggle(option)}
                      className={`
                        flex items-center gap-2 p-2 rounded cursor-pointer transition-colors duration-150
                        ${isSelected ? 'bg-brand-primary/20' : 'hover:bg-brand-dark-lighter'}
                      `}
                    >
                      <span className={`
                        w-4 h-4 rounded flex items-center justify-center text-[0.7rem]
                        ${isSelected
                          ? 'border-2 border-brand-primary bg-brand-primary text-brand-dark'
                          : 'border-2 border-brand-dark-lighter bg-transparent'
                        }
                      `}>
                        {isSelected && <Check className="w-2.5 h-2.5" />}
                      </span>
                      <span className="text-sm text-white">{option}</span>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

/** Title-style dropdown for the section header (Cliente filter) */
function TitleClienteDropdown({ options, selected, onToggle, onClear }: {
  options: string[]
  selected: Set<string>
  onToggle: (option: string) => void
  onClear: () => void
}) {
  const [open, setOpen] = useState(false)
  const buttonRef = useState<HTMLButtonElement | null>(null)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 })
  const hasSelection = selected.size > 0

  const triggerLabel = !hasSelection
    ? 'Todas as Notas Emitidas'
    : selected.size === 1
      ? Array.from(selected)[0]
      : `${selected.size} clientes selecionados`

  const handleOpen = () => {
    if (!open && buttonRef[0]) {
      const rect = buttonRef[0].getBoundingClientRect()
      setDropdownPos({ top: rect.bottom + 4, left: clampDropdownLeft(rect.left, 280) })
    }
    setOpen(!open)
  }

  return (
    <div className="relative">
      <button
        ref={(el) => { buttonRef[0] = el }}
        onClick={handleOpen}
        className="flex items-center gap-2 cursor-pointer text-lg font-semibold text-white hover:text-brand-primary transition-colors"
      >
        <span>{triggerLabel}</span>
        <ChevronDown className={`w-4 h-4 text-brand-gray transition-transform ${open ? 'rotate-180' : ''}`} />
        {!hasSelection && (
          <span className="text-xs font-normal text-brand-gray/60">(ou selecione clientes)</span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="fixed z-50 bg-brand-dark-light border border-brand-dark-lighter rounded-lg shadow-monumenta-lg min-w-[280px] max-h-[360px] overflow-y-auto p-2"
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
          >
            <div
              onClick={() => { onClear(); setOpen(false) }}
              className={`
                flex items-center gap-2 p-2 rounded cursor-pointer transition-colors duration-150 mb-1
                ${!hasSelection ? 'bg-brand-primary/20 text-brand-primary' : 'text-white hover:bg-brand-dark-lighter'}
              `}
            >
              <span className={`
                w-4 h-4 rounded flex items-center justify-center text-[0.7rem]
                ${!hasSelection
                  ? 'border-2 border-brand-primary bg-brand-primary text-brand-dark'
                  : 'border-2 border-brand-dark-lighter bg-transparent'
                }
              `}>
                {!hasSelection && <Check className="w-2.5 h-2.5" />}
              </span>
              <span className="text-sm">Todas as Notas Emitidas</span>
            </div>
            {options.map((option) => {
              const isSelected = selected.has(option)
              return (
                <div
                  key={option}
                  onClick={() => { onToggle(option); setOpen(false) }}
                  className={`
                    flex items-center gap-2 p-2 rounded cursor-pointer transition-colors duration-150
                    ${isSelected ? 'bg-brand-primary/20' : 'hover:bg-brand-dark-lighter'}
                  `}
                >
                  <span className={`
                    w-4 h-4 rounded flex items-center justify-center text-[0.7rem]
                    ${isSelected
                      ? 'border-2 border-brand-primary bg-brand-primary text-brand-dark'
                      : 'border-2 border-brand-dark-lighter bg-transparent'
                    }
                  `}>
                    {isSelected && <Check className="w-2.5 h-2.5" />}
                  </span>
                  <span className="text-sm text-white">{option}</span>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

/** Range slider filter dropdown (for numeric ranges like valores, percentuais) */
function RangeFilterDropdown({
  label,
  min,
  max,
  step = 1,
  value,
  onChange,
  onClear,
  formatValue,
}: {
  label: string
  min: number
  max: number
  step?: number
  value: [number, number] | null
  onChange: (range: [number, number]) => void
  onClear: () => void
  formatValue: (n: number) => string
}) {
  const [open, setOpen] = useState(false)
  const [buttonEl, setButtonEl] = useState<HTMLButtonElement | null>(null)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 })
  const active = value !== null
  const currentMin = value?.[0] ?? min
  const currentMax = value?.[1] ?? max

  const handleOpen = () => {
    if (!open && buttonEl) {
      const rect = buttonEl.getBoundingClientRect()
      setDropdownPos({ top: rect.bottom + 4, left: clampDropdownLeft(rect.left, 320) })
    }
    setOpen(!open)
  }

  const pctMin = max === min ? 0 : ((currentMin - min) / (max - min)) * 100
  const pctMax = max === min ? 100 : ((currentMax - min) / (max - min)) * 100

  const handleMinChange = (n: number) => {
    const safeMin = Math.max(min, Math.min(n, currentMax - step))
    onChange([safeMin, currentMax])
  }
  const handleMaxChange = (n: number) => {
    const safeMax = Math.min(max, Math.max(n, currentMin + step))
    onChange([currentMin, safeMax])
  }

  return (
    <div className="relative">
      <button
        ref={setButtonEl}
        onClick={handleOpen}
        className={`
          flex items-center gap-2 py-1.5 px-3 rounded-lg cursor-pointer text-xs font-medium transition-all duration-150
          ${active
            ? 'border-2 border-brand-primary bg-brand-primary/20 text-brand-primary'
            : 'border border-brand-dark-lighter bg-brand-dark-lighter text-brand-gray hover:bg-brand-dark hover:text-white'
          }
        `}
      >
        {label}
        {active && (
          <span className="text-[10px] text-brand-primary/80">
            {formatValue(currentMin)} – {formatValue(currentMax)}
          </span>
        )}
        <span className="text-xs">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="fixed z-50 bg-brand-dark-light border border-brand-dark-lighter rounded-lg shadow-monumenta-lg w-[320px] p-4"
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-semibold text-white">{label}</span>
              {active && (
                <button
                  onClick={() => { onClear(); setOpen(false) }}
                  className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300"
                >
                  <X size={10} />
                  Limpar
                </button>
              )}
            </div>

            {/* Current range display */}
            <div className="flex items-center justify-between text-xs text-brand-gray mb-2 font-medium">
              <span>{formatValue(currentMin)}</span>
              <span>{formatValue(currentMax)}</span>
            </div>

            {/* Dual range slider */}
            <div className="relative h-6 mb-4">
              <div className="absolute top-1/2 left-0 right-0 h-1 -translate-y-1/2 bg-brand-dark-lighter rounded-full" />
              <div
                className="absolute top-1/2 h-1 -translate-y-1/2 bg-brand-primary rounded-full"
                style={{ left: `${pctMin}%`, right: `${100 - pctMax}%` }}
              />
              <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={currentMin}
                onChange={(e) => handleMinChange(Number(e.target.value))}
                className="range-dual absolute top-0 left-0 w-full h-6"
                style={{ pointerEvents: 'none' }}
              />
              <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={currentMax}
                onChange={(e) => handleMaxChange(Number(e.target.value))}
                className="range-dual absolute top-0 left-0 w-full h-6"
                style={{ pointerEvents: 'none' }}
              />
            </div>

            {/* Numeric inputs for precision */}
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <label className="text-[10px] uppercase tracking-wider text-brand-gray/70 block mb-1">De</label>
                <input
                  type="number"
                  min={min}
                  max={max}
                  step={step}
                  value={currentMin}
                  onChange={(e) => handleMinChange(Number(e.target.value))}
                  className="w-full px-2 py-1.5 rounded bg-brand-dark-lighter border border-brand-dark-lighter text-xs text-white focus:outline-none focus:ring-2 focus:ring-brand-primary"
                />
              </div>
              <div className="flex-1">
                <label className="text-[10px] uppercase tracking-wider text-brand-gray/70 block mb-1">Até</label>
                <input
                  type="number"
                  min={min}
                  max={max}
                  step={step}
                  value={currentMax}
                  onChange={(e) => handleMaxChange(Number(e.target.value))}
                  className="w-full px-2 py-1.5 rounded bg-brand-dark-lighter border border-brand-dark-lighter text-xs text-white focus:outline-none focus:ring-2 focus:ring-brand-primary"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

/** Date range filter dropdown */
function DateRangeFilterDropdown({
  label,
  value,
  onChange,
  onClear,
}: {
  label: string
  value: { from: string; to: string } | null
  onChange: (range: { from: string; to: string }) => void
  onClear: () => void
}) {
  const [open, setOpen] = useState(false)
  const [buttonEl, setButtonEl] = useState<HTMLButtonElement | null>(null)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 })
  const active = value !== null

  const handleOpen = () => {
    if (!open && buttonEl) {
      const rect = buttonEl.getBoundingClientRect()
      setDropdownPos({ top: rect.bottom + 4, left: clampDropdownLeft(rect.left, 300) })
    }
    setOpen(!open)
  }

  const handleFromChange = (from: string) => {
    onChange({ from, to: value?.to ?? '' })
  }
  const handleToChange = (to: string) => {
    onChange({ from: value?.from ?? '', to })
  }

  const displayText = active
    ? `${value!.from ? formatDateBR(value!.from) : '—'} → ${value!.to ? formatDateBR(value!.to) : '—'}`
    : ''

  return (
    <div className="relative">
      <button
        ref={setButtonEl}
        onClick={handleOpen}
        className={`
          flex items-center gap-2 py-1.5 px-3 rounded-lg cursor-pointer text-xs font-medium transition-all duration-150
          ${active
            ? 'border-2 border-brand-primary bg-brand-primary/20 text-brand-primary'
            : 'border border-brand-dark-lighter bg-brand-dark-lighter text-brand-gray hover:bg-brand-dark hover:text-white'
          }
        `}
      >
        <Calendar size={12} />
        {label}
        {active && <span className="text-[10px] text-brand-primary/80">{displayText}</span>}
        <span className="text-xs">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="fixed z-50 bg-brand-dark-light border border-brand-dark-lighter rounded-lg shadow-monumenta-lg w-[300px] p-4"
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-white">{label}</span>
              {active && (
                <button
                  onClick={() => { onClear(); setOpen(false) }}
                  className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300"
                >
                  <X size={10} />
                  Limpar
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <div className="flex-1">
                <label className="text-[10px] uppercase tracking-wider text-brand-gray/70 block mb-1">De</label>
                <input
                  type="date"
                  value={value?.from ?? ''}
                  onChange={(e) => handleFromChange(e.target.value)}
                  className="w-full px-2 py-1.5 rounded bg-brand-dark-lighter border border-brand-dark-lighter text-xs text-white focus:outline-none focus:ring-2 focus:ring-brand-primary"
                />
              </div>
              <div className="flex-1">
                <label className="text-[10px] uppercase tracking-wider text-brand-gray/70 block mb-1">Até</label>
                <input
                  type="date"
                  value={value?.to ?? ''}
                  onChange={(e) => handleToChange(e.target.value)}
                  className="w-full px-2 py-1.5 rounded bg-brand-dark-lighter border border-brand-dark-lighter text-xs text-white focus:outline-none focus:ring-2 focus:ring-brand-primary"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

/** Active filter chips — FilterChips visual pattern */
function ActiveFilterChips({ filters, onRemove, onClearAll }: {
  filters: { key: string; label: string; value: string }[]
  onRemove: (key: string, value: string) => void
  onClearAll: () => void
}) {
  if (filters.length === 0) return null

  return (
    <div className="flex gap-2 flex-wrap items-center mt-3" role="region" aria-label="Filtros ativos">
      {filters.map((chip, i) => (
        <span
          key={`${chip.key}-${chip.value}-${i}`}
          className="inline-flex items-center gap-1 py-1 px-3 bg-brand-dark border border-brand-primary rounded-full text-xs text-brand-primary font-body"
        >
          <strong className="font-display">{chip.label}:</strong> {chip.value}
          <button
            type="button"
            onClick={() => onRemove(chip.key, chip.value)}
            aria-label={`Remover filtro ${chip.label}: ${chip.value}`}
            className="bg-transparent border-none cursor-pointer px-1 text-base text-brand-primary hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded"
          >
            ×
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={onClearAll}
        aria-label={`Limpar todos os ${filters.length} filtros`}
        className="inline-flex items-center gap-1.5 py-1 px-3 rounded-full text-xs text-error border border-border bg-transparent cursor-pointer font-display hover:bg-brand-dark-lighter transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
      >
        Limpar todos
        <span className="py-0.5 px-1.5 rounded-full bg-error/20 text-error text-[0.65rem]">
          {filters.length}
        </span>
      </button>
    </div>
  )
}

/** Status toggle button — reused across filter panels */
function StatusToggle({ status, label, active, onClick }: {
  status: NotaStatus
  label?: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`
        px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 cursor-pointer
        focus:outline-none focus:ring-2 focus:ring-brand-primary focus:z-10
        ${active
          ? 'bg-brand-primary text-brand-dark-deepest font-semibold'
          : 'bg-transparent text-brand-gray hover:bg-brand-dark-light hover:text-brand-primary'
        }
      `}
    >
      {label || STATUS_LABELS[status]}
    </button>
  )
}

/**
 * Notas Emitidas filter panel — DREControlsPanel layout.
 * border rounded-xl, labeled rows, expandable secondary filters.
 */
function NotasFilterPanel(props: {
  searchQuery: string
  onSearchChange: (v: string) => void
  clienteFilter: Set<string>
  onClienteToggle: (v: string) => void
  onClienteClear: () => void
  uniqueClientes: string[]
  statusFilter: NotaStatus | null
  onStatusChange: (v: NotaStatus | null) => void
  tipoFilter: Set<string>
  onTipoToggle: (v: string) => void
  onTipoClear: () => void
  tipoContratoFilter: Set<string>
  onTipoContratoToggle: (v: string) => void
  onTipoContratoClear: () => void
  tipoServicoFilter: Set<string>
  onTipoServicoToggle: (v: string) => void
  onTipoServicoClear: () => void
  servicoFilter: Set<string>
  onServicoToggle: (v: string) => void
  onServicoClear: () => void
  uniqueServicos: string[]
  contratoFilter: Set<string>
  onContratoToggle: (item: string) => void
  onContratoClear: () => void
  uniqueContratos: string[]
  pitFilter: Set<string>
  onPitToggle: (item: string) => void
  onPitClear: () => void
  uniquePits: string[]
  favorecidoFilter: Set<string>
  onFavorecidoToggle: (item: string) => void
  onFavorecidoClear: () => void
  uniqueFavorecidos: string[]
  cnpjFilter: Set<string>
  onCnpjToggle: (item: string) => void
  onCnpjClear: () => void
  uniqueCnpjs: string[]
  anotacoesFilter: Set<string>
  onAnotacoesToggle: (v: string) => void
  onAnotacoesClear: () => void
  valorBrutoFilter: [number, number] | null
  valorBrutoBounds: [number, number]
  onValorBrutoChange: (v: [number, number]) => void
  onValorBrutoClear: () => void
  valorLiquidoFilter: [number, number] | null
  valorLiquidoBounds: [number, number]
  onValorLiquidoChange: (v: [number, number]) => void
  onValorLiquidoClear: () => void
  retencaoFilter: [number, number] | null
  retencaoBounds: [number, number]
  onRetencaoChange: (v: [number, number]) => void
  onRetencaoClear: () => void
  emissaoRangeFilter: { from: string; to: string } | null
  onEmissaoRangeChange: (v: { from: string; to: string }) => void
  onEmissaoRangeClear: () => void
  vencimentoRangeFilter: { from: string; to: string } | null
  onVencimentoRangeChange: (v: { from: string; to: string }) => void
  onVencimentoRangeClear: () => void
  baixaRangeFilter: { from: string; to: string } | null
  onBaixaRangeChange: (v: { from: string; to: string }) => void
  onBaixaRangeClear: () => void
  activeChips: { key: string; label: string; value: string }[]
  onRemoveChip: (key: string, value: string) => void
  onClearAll: () => void
}) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="border border-brand-dark-lighter rounded-xl overflow-hidden">
      {/* Primary row: Search + Cliente + Status + Expand */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3 flex-wrap flex-1">
          {/* Search inline — at the start */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-gray/60" />
            <input
              type="text"
              value={props.searchQuery}
              onChange={(e) => props.onSearchChange(e.target.value)}
              placeholder="Buscar número, cliente..."
              className="pl-8 pr-8 py-1.5 rounded-lg text-xs bg-brand-dark-lighter border border-brand-dark-lighter text-white w-[220px] font-body
                         focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
            {props.searchQuery && (
              <button onClick={() => props.onSearchChange('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                <X className="w-3.5 h-3.5 text-brand-gray/60 hover:text-white" />
              </button>
            )}
          </div>

          {/* Cliente */}
          <FilterDropdown
            label="Cliente"
            options={props.uniqueClientes}
            selected={props.clienteFilter}
            onToggle={props.onClienteToggle}
            onClear={props.onClienteClear}
          />

          {/* Status segmented */}
          <div className="inline-flex rounded-lg border border-brand-dark-lighter overflow-hidden bg-brand-dark-lighter">
            {(['pago', 'aberto', 'vencido', 'cancelada'] as NotaStatus[]).map((s) => (
              <StatusToggle
                key={s}
                status={s}
                active={props.statusFilter === s}
                onClick={() => props.onStatusChange(props.statusFilter === s ? null : s)}
              />
            ))}
          </div>
        </div>

        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 text-xs text-brand-gray hover:text-white transition-colors ml-4 flex-shrink-0"
        >
          <span>{expanded ? 'Recolher filtros' : 'Mais filtros'}</span>
          <ChevronDown
            size={14}
            className={`transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {/* Expandable row with all other filters */}
      <div
        className="grid transition-[grid-template-rows] duration-300 ease-in-out"
        style={{ gridTemplateRows: expanded ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="px-4 pb-4 border-t border-brand-dark-lighter pt-4">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Categoria/tipo dropdowns */}
              <FilterDropdown
                label="Tipo"
                options={['NF', 'ND']}
                selected={props.tipoFilter}
                onToggle={props.onTipoToggle}
                onClear={props.onTipoClear}
              />
              <FilterDropdown
                label="Tipo de Contrato"
                options={['Digital', 'Promo']}
                selected={props.tipoContratoFilter}
                onToggle={props.onTipoContratoToggle}
                onClear={props.onTipoContratoClear}
              />
              <FilterDropdown
                label="Tipo de Serviço"
                options={['Digital', 'Promo']}
                selected={props.tipoServicoFilter}
                onToggle={props.onTipoServicoToggle}
                onClear={props.onTipoServicoClear}
              />
              <FilterDropdown
                label="Serviço"
                options={props.uniqueServicos}
                selected={props.servicoFilter}
                onToggle={props.onServicoToggle}
                onClear={props.onServicoClear}
              />
              <FilterDropdown
                label="Contrato nº"
                options={props.uniqueContratos}
                selected={props.contratoFilter}
                onToggle={props.onContratoToggle}
                onClear={props.onContratoClear}
              />
              <FilterDropdown
                label="PIT"
                options={props.uniquePits}
                selected={props.pitFilter}
                onToggle={props.onPitToggle}
                onClear={props.onPitClear}
              />
              <SearchableFilterDropdown
                label="Favorecido"
                options={props.uniqueFavorecidos}
                selected={props.favorecidoFilter}
                onToggle={props.onFavorecidoToggle}
                onClear={props.onFavorecidoClear}
              />
              <SearchableFilterDropdown
                label="CNPJ"
                options={props.uniqueCnpjs}
                selected={props.cnpjFilter}
                onToggle={props.onCnpjToggle}
                onClear={props.onCnpjClear}
              />
              <FilterDropdown
                label="Anotações"
                options={['Sim', 'Não']}
                selected={props.anotacoesFilter}
                onToggle={props.onAnotacoesToggle}
                onClear={props.onAnotacoesClear}
              />

              {/* Value range sliders */}
              <RangeFilterDropdown
                label="Valor Bruto"
                min={props.valorBrutoBounds[0]}
                max={props.valorBrutoBounds[1]}
                step={100}
                value={props.valorBrutoFilter}
                onChange={props.onValorBrutoChange}
                onClear={props.onValorBrutoClear}
                formatValue={formatCurrencyBR}
              />
              <RangeFilterDropdown
                label="Valor Líquido"
                min={props.valorLiquidoBounds[0]}
                max={props.valorLiquidoBounds[1]}
                step={100}
                value={props.valorLiquidoFilter}
                onChange={props.onValorLiquidoChange}
                onClear={props.onValorLiquidoClear}
                formatValue={formatCurrencyBR}
              />
              <RangeFilterDropdown
                label="Retenção"
                min={props.retencaoBounds[0]}
                max={props.retencaoBounds[1]}
                step={0.5}
                value={props.retencaoFilter}
                onChange={props.onRetencaoChange}
                onClear={props.onRetencaoClear}
                formatValue={(n) => `${n.toFixed(1)}%`}
              />

              {/* Date ranges */}
              <DateRangeFilterDropdown
                label="Emissão"
                value={props.emissaoRangeFilter}
                onChange={props.onEmissaoRangeChange}
                onClear={props.onEmissaoRangeClear}
              />
              <DateRangeFilterDropdown
                label="Vencimento"
                value={props.vencimentoRangeFilter}
                onChange={props.onVencimentoRangeChange}
                onClear={props.onVencimentoRangeClear}
              />
              <DateRangeFilterDropdown
                label="Baixa"
                value={props.baixaRangeFilter}
                onChange={props.onBaixaRangeChange}
                onClear={props.onBaixaRangeClear}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Active filter chips */}
      {props.activeChips.length > 0 && (
        <div className="px-4 pb-3">
          <ActiveFilterChips
            filters={props.activeChips}
            onRemove={props.onRemoveChip}
            onClearAll={props.onClearAll}
          />
        </div>
      )}
    </div>
  )
}

/** Column chips bar — sits above the table */
function ColumnChipsBar({ allColumns, visibleColumns, onToggle, presets, activePreset, onSelectPreset, customPresets, onSavePreset, onDeletePreset }: {
  allColumns: { key: string; label: string }[]
  visibleColumns: Set<string>
  onToggle: (key: string) => void
  presets: ColumnPreset[]
  activePreset: string | null
  onSelectPreset: (preset: ColumnPreset) => void
  customPresets: ColumnPreset[]
  onSavePreset: (name: string) => void
  onDeletePreset: (id: string) => void
}) {
  const [showPresets, setShowPresets] = useState(false)
  const [savingName, setSavingName] = useState('')
  const [showSaveInput, setShowSaveInput] = useState(false)
  const [showMoreHidden, setShowMoreHidden] = useState(false)

  // Only show chips for HIDDEN columns — acting as "+ add back" suggestions
  const hiddenColumns = allColumns.filter((c) => !visibleColumns.has(c.key))
  const MAX_INLINE_CHIPS = 6
  const inlineHidden = hiddenColumns.slice(0, MAX_INLINE_CHIPS)
  const overflowHidden = hiddenColumns.slice(MAX_INLINE_CHIPS)

  const chipClass = `
    flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium cursor-pointer
    border border-dashed border-brand-dark-lighter bg-transparent
    text-brand-gray/60 hover:text-white hover:border-brand-gray/40 hover:bg-brand-dark-lighter
    transition-all duration-150
  `

  return (
    <div className="px-4 pt-3 pb-2 border-b border-brand-dark-lighter">
      <div className="flex items-center gap-2 flex-wrap">
        {/* Preset selector — on the left */}
        <div className="relative">
          <button
            onClick={() => setShowPresets(!showPresets)}
            className="flex items-center gap-1.5 py-1.5 px-2.5 rounded-lg text-xs font-medium border border-brand-dark-lighter bg-brand-dark text-brand-gray hover:text-white transition-all cursor-pointer"
          >
            <Settings2 className="w-3.5 h-3.5" />
            Visões
            <ChevronDown className={`w-3 h-3 transition-transform ${showPresets ? 'rotate-180' : ''}`} />
          </button>

          {showPresets && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowPresets(false)} />
              <div className="absolute top-full mt-1 left-0 z-50 bg-brand-dark-light border border-brand-dark-lighter rounded-lg shadow-monumenta-lg w-[240px] p-2">
                {[...presets, ...customPresets].map((preset) => (
                  <div
                    key={preset.id}
                    onClick={() => { onSelectPreset(preset); setShowPresets(false) }}
                    className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors duration-150 ${
                      activePreset === preset.id ? 'bg-brand-primary/20 text-brand-primary' : 'hover:bg-brand-dark-lighter text-brand-gray hover:text-white'
                    }`}
                  >
                    <span className="text-sm font-display">{preset.name}</span>
                    <div className="flex items-center gap-1">
                      {activePreset === preset.id && <Check className="w-3.5 h-3.5 text-brand-primary" />}
                      {!preset.isDefault && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onDeletePreset(preset.id) }}
                          className="p-0.5 rounded hover:bg-brand-dark text-red-400/60 hover:text-red-400"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                <div className="border-t border-brand-dark-lighter mt-1 pt-1">
                  {showSaveInput ? (
                    <div className="flex items-center gap-1 p-1">
                      <input
                        type="text"
                        value={savingName}
                        onChange={(e) => setSavingName(e.target.value)}
                        placeholder="Nome da visão..."
                        className="flex-1 px-2 py-1 rounded text-xs bg-brand-dark text-white border border-brand-dark-lighter focus:outline-none focus:border-brand-primary font-body"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && savingName.trim()) {
                            onSavePreset(savingName.trim())
                            setSavingName('')
                            setShowSaveInput(false)
                          }
                          if (e.key === 'Escape') setShowSaveInput(false)
                        }}
                      />
                      <button
                        onClick={() => {
                          if (savingName.trim()) {
                            onSavePreset(savingName.trim())
                            setSavingName('')
                            setShowSaveInput(false)
                          }
                        }}
                        className="p-1 rounded hover:bg-brand-dark-lighter text-brand-primary"
                      >
                        <Save className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowSaveInput(true)}
                      className="flex items-center gap-1.5 p-2 text-xs text-brand-primary hover:text-brand-primary/80 font-display w-full"
                    >
                      <Plus className="w-3 h-3" /> Salvar visão atual
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Divider between Visões and hidden-column chips */}
        <div className="w-px h-5 bg-brand-dark-lighter" />

        {/* Hidden column chips — "add back" suggestions */}
        {hiddenColumns.length === 0 ? (
          <span className="text-[11px] text-brand-gray/40 italic">Todas as colunas visíveis</span>
        ) : (
          <>
            <span className="text-[10px] font-semibold text-brand-gray/50 uppercase tracking-wider mr-1">
              Adicionar:
            </span>
            {inlineHidden.map((col) => (
              <button
                key={col.key}
                onClick={() => onToggle(col.key)}
                className={chipClass}
                title={`Mostrar coluna "${col.label}"`}
              >
                <Plus size={10} />
                {col.label}
              </button>
            ))}
            {overflowHidden.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setShowMoreHidden((v) => !v)}
                  className={chipClass}
                >
                  +{overflowHidden.length} mais
                  <ChevronDown size={10} className={`transition-transform ${showMoreHidden ? 'rotate-180' : ''}`} />
                </button>
                {showMoreHidden && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowMoreHidden(false)} />
                    <div className="absolute top-full mt-1 left-0 z-50 bg-brand-dark-light border border-brand-dark-lighter rounded-lg shadow-monumenta-lg min-w-[200px] p-1">
                      {overflowHidden.map((col) => (
                        <button
                          key={col.key}
                          onClick={() => { onToggle(col.key) }}
                          className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-xs text-brand-gray hover:text-white hover:bg-brand-dark-lighter transition-colors text-left"
                        >
                          <Plus size={12} className="text-brand-gray/60" />
                          {col.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// NOTA DETAIL MODAL
// ============================================================================

const NOTES_PERIOD_KEY = 'central-de-notas'

function getNotesMetricKey(id: string): string {
  return `nota-${id}`
}

/** Inline annotation item with edit/delete */
function AnnotationItem({ note, currentUserId, onUpdate, onDelete }: {
  note: CardNote
  currentUserId: string
  onUpdate: (id: string, content: string) => void
  onDelete: (id: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [editText, setEditText] = useState(note.content)
  const isOwner = note.authorId === currentUserId

  const handleSave = () => {
    if (editText.trim()) {
      onUpdate(note.id, editText.trim())
      setEditing(false)
    }
  }

  const ts = new Date(note.updatedAt ?? note.createdAt)
  const timeLabel = ts.toLocaleDateString('pt-BR') + ' ' + ts.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

  return (
    <div className="border border-[#2A2D34] rounded-lg p-3">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[11px] font-semibold text-white truncate">{note.authorName}</span>
          <span className="text-[10px] text-brand-gray">{timeLabel}</span>
          {note.updatedAt && <span className="text-[9px] text-brand-gray italic">(editado)</span>}
        </div>
        {isOwner && !editing && (
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <button type="button" onClick={() => { setEditText(note.content); setEditing(true) }} className="p-1 rounded text-brand-gray hover:text-white transition-colors">
              <Pencil size={12} />
            </button>
            <button type="button" onClick={() => onDelete(note.id)} className="p-1 rounded text-brand-gray hover:text-red-400 transition-colors">
              <Trash2 size={12} />
            </button>
          </div>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={2} autoFocus
            className="w-full px-2 py-1.5 rounded bg-[#0D0E10] border border-[#2A2D34] text-white text-xs placeholder:text-brand-gray focus:border-brand-primary focus:outline-none resize-y" />
          <div className="flex justify-end gap-1.5">
            <button type="button" onClick={() => setEditing(false)} className="p-1 rounded text-brand-gray hover:text-white transition-colors"><X size={14} /></button>
            <button type="button" onClick={handleSave} disabled={!editText.trim()} className="p-1 rounded text-brand-primary hover:text-brand-primary/80 transition-colors disabled:opacity-40"><Check size={14} /></button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-brand-gray-light leading-relaxed whitespace-pre-wrap">{note.content}</p>
      )}
    </div>
  )
}

/** Detail field row */
function DetailField({ label, children, icon }: { label: string; children: ReactNode; icon?: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold text-brand-gray/70 uppercase tracking-wider flex items-center gap-1.5">
        {icon}
        {label}
      </span>
      <span className="text-sm text-white font-medium">{children}</span>
    </div>
  )
}

/** Big highlighted value card (Valor Bruto / Líquido / Retenção) */
function HeroValueCard({
  label,
  value,
  subtitle,
  accent = 'neutral',
  icon,
}: {
  label: string
  value: ReactNode
  subtitle?: ReactNode
  accent?: 'primary' | 'success' | 'warning' | 'neutral'
  icon?: ReactNode
}) {
  const accentStyles = {
    primary: 'border-brand-primary/30 bg-gradient-to-br from-brand-primary/10 via-brand-primary/5 to-transparent',
    success: 'border-emerald-400/30 bg-gradient-to-br from-emerald-400/10 via-emerald-400/5 to-transparent',
    warning: 'border-amber-400/30 bg-gradient-to-br from-amber-400/10 via-amber-400/5 to-transparent',
    neutral: 'border-[#2A2D34] bg-[#15161A]',
  }
  const textStyles = {
    primary: 'text-brand-primary',
    success: 'text-emerald-400',
    warning: 'text-amber-400',
    neutral: 'text-white',
  }
  return (
    <div className={`relative rounded-xl border p-4 ${accentStyles[accent]}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold text-brand-gray/80 uppercase tracking-wider mb-2">
        {icon}
        {label}
      </div>
      <div className={`text-xl font-bold ${textStyles[accent]}`}>{value}</div>
      {subtitle && (
        <div className="text-[11px] text-brand-gray mt-1">{subtitle}</div>
      )}
    </div>
  )
}

/** Visual date timeline — Emissão → Vencimento → Baixa/Pagamento */
function DateTimeline({
  emissao,
  vencimento,
  final,
  finalLabel = 'Baixa',
  status,
}: {
  emissao: string
  vencimento: string
  final: string | null
  finalLabel?: string
  status?: NotaStatus
}) {
  const finalReached = !!final
  const isVencido = status === 'vencido'

  return (
    <div className="flex items-center gap-2 w-full">
      {/* Emissão */}
      <div className="flex-1">
        <div className="text-[10px] font-semibold text-brand-gray/70 uppercase tracking-wider mb-1 flex items-center gap-1">
          <Calendar size={10} />
          Emissão
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-[#2A2D34] bg-[#15161A] px-3 py-2">
          <div className="w-2 h-2 rounded-full bg-brand-primary shrink-0" />
          <span className="text-sm text-white font-medium">{formatDateBR(emissao)}</span>
        </div>
      </div>

      <ArrowRight size={14} className="text-brand-gray/40 shrink-0 mt-5" />

      {/* Vencimento */}
      <div className="flex-1">
        <div className="text-[10px] font-semibold text-brand-gray/70 uppercase tracking-wider mb-1 flex items-center gap-1">
          <Calendar size={10} />
          Vencimento
        </div>
        <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${
          isVencido ? 'border-red-500/40 bg-red-500/5' : 'border-[#2A2D34] bg-[#15161A]'
        }`}>
          <div className={`w-2 h-2 rounded-full shrink-0 ${isVencido ? 'bg-red-400' : 'bg-yellow-400'}`} />
          <span className="text-sm text-white font-medium">{formatDateBR(vencimento)}</span>
        </div>
      </div>

      <ArrowRight size={14} className="text-brand-gray/40 shrink-0 mt-5" />

      {/* Final (Baixa/Pagamento) */}
      <div className="flex-1">
        <div className="text-[10px] font-semibold text-brand-gray/70 uppercase tracking-wider mb-1 flex items-center gap-1">
          <Calendar size={10} />
          {finalLabel}
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-[#2A2D34] bg-[#15161A] px-3 py-2">
          <div className={`w-2 h-2 rounded-full shrink-0 ${finalReached ? 'bg-brand-gray' : 'bg-brand-gray/40'}`} />
          <span className={`text-sm font-medium ${finalReached ? 'text-white' : 'text-brand-gray/40'}`}>
            {final ? formatDateBR(final) : 'Pendente'}
          </span>
        </div>
      </div>
    </div>
  )
}

function NotaDetailModal({ open, onClose, nota }: {
  open: boolean
  onClose: () => void
  nota?: NotaFiscal
}) {
  const { user } = useAuth()
  const { addNote, updateNote, deleteNote } = useCardNotesStore()
  const allNotes = useCardNotesStore((s) => s.notes)
  const [newNote, setNewNote] = useState('')

  // Reminder/cobrança state (for vencido notes)
  const [reminderOpen, setReminderOpen] = useState(false)
  const [reminderTo, setReminderTo] = useState('')
  const [reminderSubject, setReminderSubject] = useState('')
  const [reminderMessage, setReminderMessage] = useState('')
  const [reminderSent, setReminderSent] = useState(false)

  const id = nota?.id ?? ''
  const metricKey = getNotesMetricKey(id)

  // Build default reminder email whenever the nota changes
  useEffect(() => {
    if (!nota) return
    const dias = calcDiasAtraso(nota.dataVencimento)
    setReminderTo(nota.destinatario)
    setReminderSubject(`Lembrete de cobrança — ${nota.numero} (${formatDateBR(nota.dataVencimento)})`)
    setReminderMessage(
`Prezados,

Identificamos que a ${nota.tipo === 'NF' ? 'Nota Fiscal' : 'Nota de Débito'} ${nota.numero}, emitida em ${formatDateBR(nota.dataEmissao)} no valor de ${formatCurrencyBR(nota.valorBruto)}, venceu em ${formatDateBR(nota.dataVencimento)} e encontra-se em aberto há ${dias} dia${dias === 1 ? '' : 's'}.

Solicitamos, por gentileza, a regularização do pagamento o quanto antes. Caso o pagamento já tenha sido efetuado, favor desconsiderar este lembrete e nos encaminhar o comprovante.

Qualquer dúvida, estamos à disposição.

Atenciosamente,
Equipe Financeira — Monumenta`
    )
    setReminderOpen(false)
    setReminderSent(false)
  }, [nota])

  const notes = useMemo(
    () => allNotes.filter((n) => n.metric === metricKey && n.periodKey === NOTES_PERIOD_KEY),
    [allNotes, metricKey]
  )

  const userId = user?.id ?? user?.email ?? 'unknown'
  const userName = user?.name ?? user?.email ?? 'Anônimo'
  const canNote = user && NOTES_ALLOWED_ROLES.includes(user.role)

  const handleAdd = () => {
    if (!newNote.trim()) return
    addNote({ metric: metricKey, periodKey: NOTES_PERIOD_KEY, authorId: userId, authorName: userName, content: newNote.trim() })
    setNewNote('')
  }

  const handleDelete = (noteId: string) => {
    if (window.confirm('Excluir esta anotação?')) deleteNote(noteId)
  }

  if (!open || !nota) return null

  // Status
  const status = nota.status
  const statusLabel = STATUS_LABELS[status]
  const statusColor = STATUS_COLORS[status]

  // Header metadata
  const numero = nota.numero
  const tipoBadge = nota.tipo === 'NF' ? 'Nota Fiscal' : 'Nota de Débito'
  const subtitle = nota.cliente

  // Retenção calc
  const retencaoPct = nota.valorBruto
    ? ((nota.valorBruto - nota.valorLiquido) / nota.valorBruto) * 100
    : 0
  const retencaoValor = nota.valorBruto - nota.valorLiquido

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-[#1E2024] rounded-2xl border border-[#2A2D34] w-full max-w-4xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* ====== HEADER with gradient accent ====== */}
        <div className="relative px-6 py-5 border-b border-[#2A2D34] bg-gradient-to-br from-brand-primary/[0.06] via-transparent to-transparent flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              {/* Icon badge */}
              <div className="w-12 h-12 rounded-xl bg-brand-primary/10 border border-brand-primary/20 flex items-center justify-center shrink-0">
                <FileText className="w-6 h-6 text-brand-primary" />
              </div>

              <div className="min-w-0">
                {/* Tipo label */}
                <div className="text-[10px] font-semibold text-brand-primary uppercase tracking-[0.15em] mb-1">
                  {tipoBadge}
                </div>
                {/* Número + status */}
                <div className="flex items-center gap-3 flex-wrap">
                  <h2 className="text-2xl font-bold text-white tracking-tight">{numero}</h2>
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${statusColor}`}>
                    {statusLabel}
                  </span>
                </div>
                {/* Cliente */}
                <div className="text-sm text-brand-gray mt-1 flex items-center gap-1.5">
                  <User size={13} />
                  <span className="truncate">{subtitle}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {status === 'vencido' && (
                <button
                  onClick={() => setReminderOpen((v) => !v)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                    reminderOpen
                      ? 'bg-red-500/20 border-red-500/40 text-red-300'
                      : 'bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20 hover:text-red-300'
                  }`}
                >
                  <Send className="w-3.5 h-3.5" />
                  Disparar lembrete / cobrança
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-[#2A2D34] transition-colors"
                aria-label="Fechar"
              >
                <X className="h-5 w-5 text-[#838998]" />
              </button>
            </div>
          </div>
        </div>

        {/* ====== Content ====== */}
        <div className="overflow-y-auto flex-1">

          {/* ===== Reminder / cobrança panel (only for vencido) ===== */}
          {status === 'vencido' && reminderOpen && (
            <div className="px-6 pt-6">
              <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-5">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center justify-center shrink-0">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-white">Lembrete de cobrança</h3>
                    <p className="text-xs text-brand-gray mt-0.5">
                      Revise os campos abaixo antes de enviar. O texto pode ser editado livremente.
                    </p>
                  </div>
                </div>

                {reminderSent ? (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-brand-primary/10 border border-brand-primary/30 text-brand-primary text-xs font-medium">
                    <Check className="w-4 h-4" />
                    Lembrete enviado para {reminderTo}.
                  </div>
                ) : (
                  <>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-[10px] font-semibold text-brand-gray/80 uppercase tracking-wider mb-1.5">
                          Destinatário
                        </label>
                        <input
                          type="email"
                          value={reminderTo}
                          onChange={(e) => setReminderTo(e.target.value)}
                          className="w-full px-3 py-2 rounded-lg bg-[#0D0E10] border border-[#2A2D34] text-white text-xs placeholder:text-brand-gray focus:border-brand-primary focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] font-semibold text-brand-gray/80 uppercase tracking-wider mb-1.5">
                          Assunto
                        </label>
                        <input
                          type="text"
                          value={reminderSubject}
                          onChange={(e) => setReminderSubject(e.target.value)}
                          className="w-full px-3 py-2 rounded-lg bg-[#0D0E10] border border-[#2A2D34] text-white text-xs placeholder:text-brand-gray focus:border-brand-primary focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] font-semibold text-brand-gray/80 uppercase tracking-wider mb-1.5">
                          Mensagem
                        </label>
                        <textarea
                          value={reminderMessage}
                          onChange={(e) => setReminderMessage(e.target.value)}
                          rows={10}
                          className="w-full px-3 py-2 rounded-lg bg-[#0D0E10] border border-[#2A2D34] text-white text-xs placeholder:text-brand-gray focus:border-brand-primary focus:outline-none resize-y font-mono leading-relaxed"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 mt-4">
                      <button
                        type="button"
                        onClick={() => setReminderOpen(false)}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium text-brand-gray hover:text-white hover:bg-[#2A2D34] transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (!reminderTo.trim() || !reminderMessage.trim()) return
                          if (canNote) {
                            addNote({
                              metric: metricKey,
                              periodKey: NOTES_PERIOD_KEY,
                              authorId: userId,
                              authorName: userName,
                              content: `Lembrete de cobrança enviado para ${reminderTo}.\nAssunto: ${reminderSubject}`,
                            })
                          }
                          setReminderSent(true)
                        }}
                        disabled={!reminderTo.trim() || !reminderMessage.trim()}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Send className="w-3.5 h-3.5" />
                        Enviar lembrete
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ===== HERO: Valores em destaque ===== */}
          <div className="px-6 pt-6">
            <div className="grid grid-cols-3 gap-3">
              <HeroValueCard
                label="Valor Bruto"
                value={<SensitiveValue mask="R$ ••••••">{formatCurrencyBR(nota.valorBruto)}</SensitiveValue>}
                accent="neutral"
              />
              <HeroValueCard
                label="Valor Líquido"
                value={<SensitiveValue mask="R$ ••••••">{formatCurrencyBR(nota.valorLiquido)}</SensitiveValue>}
                accent="neutral"
              />
              <HeroValueCard
                label="Retenção"
                value={`${retencaoPct.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}
                subtitle={<SensitiveValue mask="R$ ••••••">{formatCurrencyBR(retencaoValor)}</SensitiveValue>}
                accent="neutral"
                icon={<TrendingDown size={11} />}
              />
            </div>

            {/* Favorecido + CNPJ */}
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="rounded-xl border border-[#2A2D34] bg-[#15161A] p-4">
                <div className="text-[10px] font-semibold text-brand-gray/80 uppercase tracking-wider mb-2">Favorecido</div>
                <div className="text-base font-bold text-white">{nota.favorecido}</div>
              </div>
              <div className="rounded-xl border border-[#2A2D34] bg-[#15161A] p-4">
                <div className="text-[10px] font-semibold text-brand-gray/80 uppercase tracking-wider mb-2">CNPJ</div>
                <div className="text-base font-bold text-white">{nota.cnpj}</div>
              </div>
            </div>
          </div>

          {/* ===== Timeline de datas ===== */}
          <div className="px-6 pt-6">
            <h3 className="text-[10px] font-semibold text-brand-gray/80 uppercase tracking-[0.15em] mb-3">
              Linha do Tempo
            </h3>
            <DateTimeline
              emissao={nota.dataEmissao}
              vencimento={nota.dataVencimento}
              final={nota.dataBaixa}
              finalLabel="Baixa"
              status={nota.status}
            />
          </div>

          {/* ===== Info secundária em grid ===== */}
          <div className="px-6 pt-6 pb-6">
            <h3 className="text-[10px] font-semibold text-brand-gray/80 uppercase tracking-[0.15em] mb-3">
              Informações
            </h3>
            <div className="rounded-xl border border-[#2A2D34] bg-[#15161A] p-5 grid grid-cols-2 gap-x-6 gap-y-4">
              <DetailField label="Cliente" icon={<User size={10} />}>
                {nota.cliente}
              </DetailField>
              <DetailField label="Destinatário" icon={<Mail size={10} />}>
                <span className="text-brand-gray-light break-all">{nota.destinatario}</span>
              </DetailField>
              <DetailField label="Contrato nº">
                {nota.contrato || <span className="text-brand-gray/40">—</span>}
              </DetailField>
              <DetailField label="PIT">{nota.pit}</DetailField>
              <DetailField label="Serviço">{nota.tipoServico}</DetailField>
              <DetailField label="Centro Resultado">{nota.centroResultado}</DetailField>
              <DetailField label="Tipo de Serviço">
                <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase bg-transparent text-brand-gray border border-brand-gray/30">
                  {nota.servicoTipo === 'digital' ? 'DIGITAL' : 'PROMO'}
                </span>
              </DetailField>
              <DetailField label="Tipo de Contrato">
                <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase bg-transparent text-brand-gray border border-brand-gray/30">
                  {nota.digitalPromo === 'digital' ? 'DIGITAL' : 'PROMO'}
                </span>
              </DetailField>
              {nota.codigoCDA && (
                <DetailField label="CDA">{nota.codigoCDA}</DetailField>
              )}
            </div>
          </div>

          {/* ===== Annotations ===== */}
          {canNote && (
            <div className="px-6 pb-6">
              <h3 className="text-[10px] font-semibold text-brand-gray/80 uppercase tracking-[0.15em] mb-3 flex items-center gap-2">
                <MessageSquare size={12} className="text-brand-primary" />
                Anotações {notes.length > 0 && <span className="text-brand-primary">({notes.length})</span>}
              </h3>

              <div className="rounded-xl border border-[#2A2D34] bg-[#15161A] p-4">
                <div className="space-y-2">
                  {notes.length === 0 ? (
                    <p className="text-xs text-brand-gray text-center py-4">Nenhuma anotação ainda.</p>
                  ) : (
                    notes.map((note) => (
                      <AnnotationItem key={note.id} note={note} currentUserId={userId} onUpdate={updateNote} onDelete={handleDelete} />
                    ))
                  )}
                </div>

                {/* Add new */}
                <div className="mt-3 pt-3 border-t border-[#2A2D34]">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Adicionar anotação..."
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg bg-[#0D0E10] border border-[#2A2D34] text-white text-xs placeholder:text-brand-gray focus:border-brand-primary focus:outline-none resize-y"
                  />
                  <div className="flex justify-end mt-2">
                    <button
                      type="button" onClick={handleAdd} disabled={!newNote.trim()}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-primary text-brand-dark hover:bg-brand-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Adicionar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/** Helper to count annotations for a row */
function useAnnotationCount(id: string): number {
  const allNotes = useCardNotesStore((s) => s.notes)
  const metricKey = getNotesMetricKey(id)
  return useMemo(
    () => allNotes.filter((n) => n.metric === metricKey && n.periodKey === NOTES_PERIOD_KEY).length,
    [allNotes, metricKey]
  )
}

/** Annotation badge shown in the table row */
function AnnotationBadge({ id }: { id: string }) {
  const count = useAnnotationCount(id)
  if (count === 0) return null
  return (
    <span className="inline-flex items-center gap-0.5 text-brand-primary ml-1.5" title={`${count} anotação(ões)`}>
      <MessageSquare size={11} />
      <span className="text-[9px] font-semibold">{count}</span>
    </span>
  )
}

/** Dedicated annotation-count cell for the "Anotações" table column */
function AnnotationsCountCell({ id }: { id: string }) {
  const count = useAnnotationCount(id)
  if (count === 0) {
    return <span className="text-brand-gray/40">—</span>
  }
  return (
    <span className="inline-flex items-center gap-1 text-brand-primary" title={`${count} anotação(ões)`}>
      <MessageSquare size={12} />
      <span className="text-xs font-semibold">{count}</span>
    </span>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function CentralDeNotas() {
  usePageTitle('Central de Notas')
  const { user } = useAuth()

  // Access control
  const allowedRoles = ['admin', 'ceo', 'vice-presidente', 'diretor-executivo']
  if (!user || !allowedRoles.includes(user.role)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Shield className="h-16 w-16 text-brand-red animate-pulse" />
        <h1 className="text-xl font-bold text-white">Acesso Restrito</h1>
        <p className="text-brand-gray">Você não tem permissão para acessar esta página.</p>
      </div>
    )
  }

  return <CentralDeNotasContent />
}

function CentralDeNotasContent() {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  // Global period filter
  const defaultDates = useMemo(() => calculatePeriodDates('ytd'), [])
  const [periodStartDate, setPeriodStartDate] = useState(defaultDates.startDate)
  const [periodEndDate, setPeriodEndDate] = useState(defaultDates.endDate)

  // Notas Emitidas filters
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<NotaStatus | null>(null)
  const [tipoFilter, setTipoFilter] = useState<Set<string>>(new Set())
  const [tipoContratoFilter, setTipoContratoFilter] = useState<Set<string>>(new Set())
  const [tipoServicoFilter, setTipoServicoFilter] = useState<Set<string>>(new Set())
  const [servicoFilter, setServicoFilter] = useState<Set<string>>(new Set())
  const [clienteFilter, setClienteFilter] = useState<Set<string>>(new Set())
  const [contratoFilter, setContratoFilter] = useState<Set<string>>(new Set())
  const [pitFilter, setPitFilter] = useState<Set<string>>(new Set())
  const [favorecidoFilter, setFavorecidoFilter] = useState<Set<string>>(new Set())
  const [cnpjFilter, setCnpjFilter] = useState<Set<string>>(new Set())
  const [anotacoesFilter, setAnotacoesFilter] = useState<Set<string>>(new Set())
  const [valorBrutoFilter, setValorBrutoFilter] = useState<[number, number] | null>(null)
  const [valorLiquidoFilter, setValorLiquidoFilter] = useState<[number, number] | null>(null)
  const [retencaoFilter, setRetencaoFilter] = useState<[number, number] | null>(null)
  const [emissaoRangeFilter, setEmissaoRangeFilter] = useState<{ from: string; to: string } | null>(null)
  const [vencimentoRangeFilter, setVencimentoRangeFilter] = useState<{ from: string; to: string } | null>(null)
  const [baixaRangeFilter, setBaixaRangeFilter] = useState<{ from: string; to: string } | null>(null)

  // Contract consumption (shared between Visão Geral and Execução Contratual)
  const { contratosComConsumo: _contratosConsumo, totals: contratosTotals } = useContratosConsumo(clienteFilter, periodStartDate, periodEndDate)

  // Selected row for detail modal
  const [selectedNota, setSelectedNota] = useState<NotaFiscal | null>(null)

  // Access notes store for annotation filtering
  const allStoreNotes = useCardNotesStore((s) => s.notes)

  // Pagination
  const [notasPage, setNotasPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)

  // KPI tab (Status / Ciclo Financeiro / Composição e Risco / Maiores Devedores)
  const [activeKpiTab, setActiveKpiTab] = useState<KpiTabId>('geral')
  const [cicloFilter, setCicloFilter] = useState<'adiantamentos' | 'atrasos' | null>(null)

  // Period change handler
  const handlePeriodChange = useCallback((dates: PeriodDates) => {
    setPeriodStartDate(dates.startDate)
    setPeriodEndDate(dates.endDate)
    setNotasPage(1)
  }, [])

  // Column presets (Notas Emitidas)
  const [visibleNotaCols, setVisibleNotaCols] = useState<Set<string>>(
    new Set(DEFAULT_NOTA_PRESETS[0].columns)
  )
  const [activePreset, setActivePreset] = useState<string | null>('completa')
  const [customPresets, setCustomPresets] = useState<ColumnPreset[]>([])

  // Sort state
  const [notasSort, setNotasSort] = useState<SortState | null>({ key: 'dataEmissao', direction: 'desc' })

  // ---------------------------------------------------------------------------
  // Derived: unique filter options
  // ---------------------------------------------------------------------------

  const uniqueClientes = useMemo(() =>
    [...new Set(MOCK_NOTAS.map((n) => n.cliente))].sort(),
    []
  )
  const uniqueContratos = useMemo(() =>
    [...new Set(MOCK_NOTAS.map((n) => n.contrato).filter(Boolean) as string[])].sort(),
    []
  )
  const uniquePits = useMemo(() =>
    [...new Set(MOCK_NOTAS.map((n) => n.pit))].sort(),
    []
  )
  const uniqueServicos = useMemo(() =>
    [...new Set(MOCK_NOTAS.map((n) => n.tipoServico))].sort(),
    []
  )
  const uniqueFavorecidos = useMemo(() =>
    [...new Set(MOCK_NOTAS.map((n) => n.favorecido))].sort(),
    []
  )
  const uniqueCnpjs = useMemo(() =>
    [...new Set(MOCK_NOTAS.map((n) => n.cnpj))].sort(),
    []
  )

  // Value range bounds (derived from data)
  const valorBrutoBounds = useMemo(() => {
    const values = MOCK_NOTAS.map((n) => n.valorBruto)
    return [Math.floor(Math.min(...values)), Math.ceil(Math.max(...values))] as [number, number]
  }, [])
  const valorLiquidoBounds = useMemo(() => {
    const values = MOCK_NOTAS.map((n) => n.valorLiquido)
    return [Math.floor(Math.min(...values)), Math.ceil(Math.max(...values))] as [number, number]
  }, [])
  const retencaoBounds: [number, number] = [0, 100]

  // ---------------------------------------------------------------------------
  // Filtered data: Notas
  // ---------------------------------------------------------------------------

  const filteredNotas = useMemo(() => {
    let data = MOCK_NOTAS

    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      data = data.filter((n) =>
        n.numero.toLowerCase().includes(q) ||
        n.cliente.toLowerCase().includes(q) ||
        n.favorecido.toLowerCase().includes(q) ||
        n.cnpj.toLowerCase().includes(q) ||
        n.tipoServico.toLowerCase().includes(q) ||
        (n.contrato && n.contrato.toLowerCase().includes(q)) ||
        n.pit.toLowerCase().includes(q) ||
        (n.codigoCDA && n.codigoCDA.toLowerCase().includes(q))
      )
    }

    // Global period filter (by data de emissão)
    data = data.filter((n) => {
      const dateValue = n.dataEmissao
      if (!dateValue) return false
      if (periodStartDate && dateValue < periodStartDate) return false
      if (periodEndDate && dateValue > periodEndDate) return false
      return true
    })

    if (statusFilter) data = data.filter((n) => n.status === statusFilter)
    if (tipoFilter.size > 0) data = data.filter((n) => tipoFilter.has(n.tipo))
    if (tipoContratoFilter.size > 0) data = data.filter((n) => tipoContratoFilter.has(n.digitalPromo === 'digital' ? 'Digital' : 'Promo'))
    if (tipoServicoFilter.size > 0) data = data.filter((n) => tipoServicoFilter.has(n.servicoTipo === 'digital' ? 'Digital' : 'Promo'))
    if (servicoFilter.size > 0) data = data.filter((n) => servicoFilter.has(n.tipoServico))
    if (clienteFilter.size > 0) data = data.filter((n) => clienteFilter.has(n.cliente))
    if (contratoFilter.size > 0) data = data.filter((n) => n.contrato && contratoFilter.has(n.contrato))
    if (pitFilter.size > 0) data = data.filter((n) => pitFilter.has(n.pit))
    if (favorecidoFilter.size > 0) data = data.filter((n) => favorecidoFilter.has(n.favorecido))
    if (cnpjFilter.size > 0) data = data.filter((n) => cnpjFilter.has(n.cnpj))

    // Value range filters
    if (valorBrutoFilter) {
      data = data.filter((n) => n.valorBruto >= valorBrutoFilter[0] && n.valorBruto <= valorBrutoFilter[1])
    }
    if (valorLiquidoFilter) {
      data = data.filter((n) => n.valorLiquido >= valorLiquidoFilter[0] && n.valorLiquido <= valorLiquidoFilter[1])
    }
    if (retencaoFilter) {
      data = data.filter((n) => {
        if (!n.valorBruto) return false
        const pct = ((n.valorBruto - n.valorLiquido) / n.valorBruto) * 100
        return pct >= retencaoFilter[0] && pct <= retencaoFilter[1]
      })
    }

    // Date range filters
    if (emissaoRangeFilter) {
      data = data.filter((n) => {
        if (emissaoRangeFilter.from && n.dataEmissao < emissaoRangeFilter.from) return false
        if (emissaoRangeFilter.to && n.dataEmissao > emissaoRangeFilter.to) return false
        return true
      })
    }
    if (vencimentoRangeFilter) {
      data = data.filter((n) => {
        if (vencimentoRangeFilter.from && n.dataVencimento < vencimentoRangeFilter.from) return false
        if (vencimentoRangeFilter.to && n.dataVencimento > vencimentoRangeFilter.to) return false
        return true
      })
    }
    if (baixaRangeFilter) {
      data = data.filter((n) => {
        if (!n.dataBaixa) return false
        if (baixaRangeFilter.from && n.dataBaixa < baixaRangeFilter.from) return false
        if (baixaRangeFilter.to && n.dataBaixa > baixaRangeFilter.to) return false
        return true
      })
    }

    // Annotation filter (Sim/Não)
    if (anotacoesFilter.size > 0 && anotacoesFilter.size < 2) {
      const wantWith = anotacoesFilter.has('Sim')
      data = data.filter((n) => {
        const hasNotes = allStoreNotes.some((sn) => sn.metric === getNotesMetricKey(n.id) && sn.periodKey === NOTES_PERIOD_KEY)
        return wantWith ? hasNotes : !hasNotes
      })
    }

    // Ciclo Financeiro card filter
    if (cicloFilter === 'adiantamentos') {
      data = data.filter((n) => !!n.adiantamento)
    } else if (cicloFilter === 'atrasos') {
      data = data.filter((n) => {
        if (n.status === 'cancelada') return false
        if (n.status === 'vencido') return true
        if (n.status === 'pago' && n.dataBaixa && n.dataBaixa > n.dataVencimento) return true
        return false
      })
    }

    return data
  }, [searchQuery, statusFilter, tipoFilter, tipoContratoFilter, tipoServicoFilter, servicoFilter, clienteFilter, contratoFilter, pitFilter, favorecidoFilter, cnpjFilter, valorBrutoFilter, valorLiquidoFilter, retencaoFilter, emissaoRangeFilter, vencimentoRangeFilter, baixaRangeFilter, periodStartDate, periodEndDate, anotacoesFilter, allStoreNotes, cicloFilter])

  // ---------------------------------------------------------------------------
  // Big Numbers
  // ---------------------------------------------------------------------------

  const notasSummary = useMemo(() => {
    const total = filteredNotas.reduce((sum, n) => sum + n.valorBruto, 0)
    const pago = filteredNotas.filter((n) => n.status === 'pago')
    const aberto = filteredNotas.filter((n) => n.status === 'aberto')
    const vencido = filteredNotas.filter((n) => n.status === 'vencido')
    const cancelada = filteredNotas.filter((n) => n.status === 'cancelada')
    return {
      total, totalCount: filteredNotas.length,
      pago: pago.reduce((s, n) => s + n.valorBruto, 0), pagoCount: pago.length,
      aberto: aberto.reduce((s, n) => s + n.valorBruto, 0), abertoCount: aberto.length,
      vencido: vencido.reduce((s, n) => s + n.valorBruto, 0), vencidoCount: vencido.length,
      cancelada: cancelada.reduce((s, n) => s + n.valorBruto, 0), canceladaCount: cancelada.length,
    }
  }, [filteredNotas])

  // ---------------------------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------------------------

  const sortedNotas = useMemo(() => sortRows(filteredNotas, notasSort), [filteredNotas, notasSort])

  const paginatedNotas = useMemo(() => {
    const start = (notasPage - 1) * pageSize
    return sortedNotas.slice(start, start + pageSize)
  }, [sortedNotas, notasPage, pageSize])

  const notasTotalPages = Math.ceil(filteredNotas.length / pageSize)

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size)
    setNotasPage(1)
  }, [])

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const toggleSetItem = useCallback((setter: React.Dispatch<React.SetStateAction<Set<string>>>, item: string) => {
    setter((prev) => {
      const next = new Set(prev)
      if (next.has(item)) next.delete(item)
      else next.add(item)
      return next
    })
  }, [])

  const toggleNotaCol = useCallback((key: string) => {
    setVisibleNotaCols((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
    setActivePreset(null)
  }, [])

  const selectPreset = useCallback((preset: ColumnPreset) => {
    setVisibleNotaCols(new Set(preset.columns))
    setActivePreset(preset.id)
  }, [])

  const saveCustomPreset = useCallback((name: string) => {
    const id = `custom-${Date.now()}`
    setCustomPresets((prev) => [...prev, {
      id, name, columns: [...visibleNotaCols],
    }])
    setActivePreset(id)
  }, [visibleNotaCols])

  const deleteCustomPreset = useCallback((id: string) => {
    setCustomPresets((prev) => prev.filter((p) => p.id !== id))
    if (activePreset === id) setActivePreset(null)
  }, [activePreset])

  // ---------------------------------------------------------------------------
  // Clear filters + active chip builders per tab
  // ---------------------------------------------------------------------------

  const clearNotaFilters = useCallback(() => {
    setSearchQuery('')
    setStatusFilter(null)
    setTipoFilter(new Set())
    setTipoContratoFilter(new Set())
    setTipoServicoFilter(new Set())
    setServicoFilter(new Set())
    setClienteFilter(new Set())
    setContratoFilter(new Set())
    setPitFilter(new Set())
    setFavorecidoFilter(new Set())
    setCnpjFilter(new Set())
    setAnotacoesFilter(new Set())
    setValorBrutoFilter(null)
    setValorLiquidoFilter(null)
    setRetencaoFilter(null)
    setEmissaoRangeFilter(null)
    setVencimentoRangeFilter(null)
    setBaixaRangeFilter(null)
    setNotasPage(1)
  }, [])

  // Build active filter chips for Notas
  const notaActiveChips = useMemo(() => {
    const chips: { key: string; label: string; value: string }[] = []
    if (statusFilter) chips.push({ key: 'status', label: 'Status', value: STATUS_LABELS[statusFilter] })
    tipoFilter.forEach((v) => chips.push({ key: 'tipo', label: 'Tipo', value: v }))
    tipoContratoFilter.forEach((v) => chips.push({ key: 'tipoContrato', label: 'Tipo de Contrato', value: v }))
    tipoServicoFilter.forEach((v) => chips.push({ key: 'tipoServico', label: 'Tipo de Serviço', value: v }))
    servicoFilter.forEach((v) => chips.push({ key: 'servico', label: 'Serviço', value: v }))
    contratoFilter.forEach((c) => chips.push({ key: 'contrato', label: 'Contrato nº', value: c }))
    pitFilter.forEach((c) => chips.push({ key: 'pit', label: 'PIT', value: c }))
    favorecidoFilter.forEach((v) => chips.push({ key: 'favorecido', label: 'Favorecido', value: v }))
    cnpjFilter.forEach((v) => chips.push({ key: 'cnpj', label: 'CNPJ', value: v }))
    anotacoesFilter.forEach((v) => chips.push({ key: 'anotacoes', label: 'Anotações', value: v }))
    if (valorBrutoFilter) chips.push({ key: 'valorBruto', label: 'Valor Bruto', value: `${formatCurrencyBR(valorBrutoFilter[0])} – ${formatCurrencyBR(valorBrutoFilter[1])}` })
    if (valorLiquidoFilter) chips.push({ key: 'valorLiquido', label: 'Valor Líquido', value: `${formatCurrencyBR(valorLiquidoFilter[0])} – ${formatCurrencyBR(valorLiquidoFilter[1])}` })
    if (retencaoFilter) chips.push({ key: 'retencao', label: 'Retenção', value: `${retencaoFilter[0].toFixed(1)}% – ${retencaoFilter[1].toFixed(1)}%` })
    if (emissaoRangeFilter) chips.push({ key: 'emissao', label: 'Emissão', value: `${emissaoRangeFilter.from ? formatDateBR(emissaoRangeFilter.from) : '—'} → ${emissaoRangeFilter.to ? formatDateBR(emissaoRangeFilter.to) : '—'}` })
    if (vencimentoRangeFilter) chips.push({ key: 'vencimento', label: 'Vencimento', value: `${vencimentoRangeFilter.from ? formatDateBR(vencimentoRangeFilter.from) : '—'} → ${vencimentoRangeFilter.to ? formatDateBR(vencimentoRangeFilter.to) : '—'}` })
    if (baixaRangeFilter) chips.push({ key: 'baixa', label: 'Baixa', value: `${baixaRangeFilter.from ? formatDateBR(baixaRangeFilter.from) : '—'} → ${baixaRangeFilter.to ? formatDateBR(baixaRangeFilter.to) : '—'}` })
    return chips
  }, [statusFilter, tipoFilter, tipoContratoFilter, tipoServicoFilter, servicoFilter, contratoFilter, pitFilter, favorecidoFilter, cnpjFilter, anotacoesFilter, valorBrutoFilter, valorLiquidoFilter, retencaoFilter, emissaoRangeFilter, vencimentoRangeFilter, baixaRangeFilter])

  const handleRemoveNotaChip = useCallback((key: string, value: string) => {
    const removeFromSet = (setFn: React.Dispatch<React.SetStateAction<Set<string>>>) =>
      setFn((prev) => { const n = new Set(prev); n.delete(value); return n })
    switch (key) {
      case 'status': setStatusFilter(null); break
      case 'tipo': removeFromSet(setTipoFilter); break
      case 'tipoContrato': removeFromSet(setTipoContratoFilter); break
      case 'tipoServico': removeFromSet(setTipoServicoFilter); break
      case 'servico': removeFromSet(setServicoFilter); break
      case 'contrato': removeFromSet(setContratoFilter); break
      case 'pit': removeFromSet(setPitFilter); break
      case 'favorecido': removeFromSet(setFavorecidoFilter); break
      case 'cnpj': removeFromSet(setCnpjFilter); break
      case 'anotacoes': removeFromSet(setAnotacoesFilter); break
      case 'valorBruto': setValorBrutoFilter(null); break
      case 'valorLiquido': setValorLiquidoFilter(null); break
      case 'retencao': setRetencaoFilter(null); break
      case 'emissao': setEmissaoRangeFilter(null); break
      case 'vencimento': setVencimentoRangeFilter(null); break
      case 'baixa': setBaixaRangeFilter(null); break
    }
    setNotasPage(1)
  }, [])

  // ---------------------------------------------------------------------------
  // Visible columns
  // ---------------------------------------------------------------------------

  const visibleNotaColumns = useMemo(
    () => NOTA_COLUMNS.filter((c) => visibleNotaCols.has(c.key)),
    [visibleNotaCols]
  )

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-5">
      {/* Period Selector — global, shared across all tabs */}
      <div className="border border-brand-dark-lighter rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4 flex-wrap flex-1">
            <span className="text-xs font-semibold text-brand-gray uppercase tracking-wide min-w-[80px]">
              Período
            </span>
            <PeriodPresetSelector
              startDate={periodStartDate}
              endDate={periodEndDate}
              onPeriodChange={handlePeriodChange}
            />
          </div>

          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-dark-lighter text-brand-gray border border-brand-dark-lighter hover:text-white transition-all cursor-pointer ml-4">
            <Download className="w-3.5 h-3.5" />
            Exportar CSV
          </button>
        </div>
      </div>

      {/* Section header: title-style Cliente dropdown (acts as section title) */}
      <div className="flex items-center gap-4 flex-wrap">
        <TitleClienteDropdown
          options={uniqueClientes}
          selected={clienteFilter}
          onToggle={(item) => { toggleSetItem(setClienteFilter, item); setNotasPage(1) }}
          onClear={() => { setClienteFilter(new Set()); setNotasPage(1) }}
        />
      </div>

      {/* ================================================================== */}
      {/* NOTAS EMITIDAS                                                     */}
      {/* ================================================================== */}
      {/* KPI Tabs */}
      <KpiTabBar
        active={activeKpiTab}
        onChange={(t) => { setActiveKpiTab(t); setCicloFilter(null) }}
        badges={{ recebimentos: notasSummary.vencidoCount }}
      />

      <AnimatedKpiTabContent
        activeTab={activeKpiTab}
        render={(tab) => {
          if (tab === 'geral') {
            return (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  <SummaryCard
                    label="Emitido no Período"
                    help="Soma do valor bruto de todas as notas no período e filtros selecionados, independente do status."
                    value={notasSummary.total}
                    count={notasSummary.totalCount}
                    color="text-white"
                  />
                  <SummaryCard
                    label="Pagas"
                    help="Notas com baixa registrada. Valor bruto somado, contagem de notas quitadas."
                    value={notasSummary.pago}
                    count={notasSummary.pagoCount}
                    color="text-brand-primary"
                  />
                  <SummaryCard
                    label="Em Aberto"
                    help="Notas emitidas ainda dentro do prazo de vencimento (sem baixa e sem atraso)."
                    value={notasSummary.aberto}
                    count={notasSummary.abertoCount}
                    color="text-white"
                  />
                  <SummaryCard
                    label="Vencidas"
                    help="Notas sem baixa que ultrapassaram a data de vencimento — indicador direto de inadimplência."
                    value={notasSummary.vencido}
                    count={notasSummary.vencidoCount}
                    color="text-red-400"
                  />
                  <SummaryCard
                    label="Canceladas"
                    help="Notas que foram emitidas e posteriormente canceladas. Não compõem receita nem inadimplência."
                    value={notasSummary.cancelada}
                    count={notasSummary.canceladaCount}
                    color="text-brand-gray"
                  />
              </div>
            )
          }
          if (tab === 'recebimentos') return <RecebimentosCards notas={filteredNotas} />
          if (tab === 'execucao') return <ExecucaoContratualCards clienteFilter={clienteFilter} periodStart={periodStartDate} periodEnd={periodEndDate} />
          if (tab === 'ciclo') return <CicloFinanceiroCards notas={filteredNotas} cicloFilter={cicloFilter} onCicloFilterChange={(v) => { setCicloFilter(v); setNotasPage(1) }} />
          if (tab === 'composicao') return <ComposicaoCards notas={filteredNotas} />

          return null
        }}
      />

          {/* Filters — DREControlsPanel pattern */}
          <NotasFilterPanel
            searchQuery={searchQuery}
            onSearchChange={(v) => { setSearchQuery(v); setNotasPage(1) }}
            clienteFilter={clienteFilter}
            onClienteToggle={(item) => { toggleSetItem(setClienteFilter, item); setNotasPage(1) }}
            onClienteClear={() => { setClienteFilter(new Set()); setNotasPage(1) }}
            uniqueClientes={uniqueClientes}
            statusFilter={statusFilter}
            onStatusChange={(v) => { setStatusFilter(v); setNotasPage(1) }}
            tipoFilter={tipoFilter}
            onTipoToggle={(item) => { toggleSetItem(setTipoFilter, item); setNotasPage(1) }}
            onTipoClear={() => { setTipoFilter(new Set()); setNotasPage(1) }}
            tipoContratoFilter={tipoContratoFilter}
            onTipoContratoToggle={(item) => { toggleSetItem(setTipoContratoFilter, item); setNotasPage(1) }}
            onTipoContratoClear={() => { setTipoContratoFilter(new Set()); setNotasPage(1) }}
            tipoServicoFilter={tipoServicoFilter}
            onTipoServicoToggle={(item) => { toggleSetItem(setTipoServicoFilter, item); setNotasPage(1) }}
            onTipoServicoClear={() => { setTipoServicoFilter(new Set()); setNotasPage(1) }}
            contratoFilter={contratoFilter}
            onContratoToggle={(item) => { toggleSetItem(setContratoFilter, item); setNotasPage(1) }}
            onContratoClear={() => { setContratoFilter(new Set()); setNotasPage(1) }}
            uniqueContratos={uniqueContratos}
            pitFilter={pitFilter}
            onPitToggle={(item) => { toggleSetItem(setPitFilter, item); setNotasPage(1) }}
            onPitClear={() => { setPitFilter(new Set()); setNotasPage(1) }}
            uniquePits={uniquePits}
            favorecidoFilter={favorecidoFilter}
            onFavorecidoToggle={(item) => { toggleSetItem(setFavorecidoFilter, item); setNotasPage(1) }}
            onFavorecidoClear={() => { setFavorecidoFilter(new Set()); setNotasPage(1) }}
            uniqueFavorecidos={uniqueFavorecidos}
            cnpjFilter={cnpjFilter}
            onCnpjToggle={(item) => { toggleSetItem(setCnpjFilter, item); setNotasPage(1) }}
            onCnpjClear={() => { setCnpjFilter(new Set()); setNotasPage(1) }}
            uniqueCnpjs={uniqueCnpjs}
            servicoFilter={servicoFilter}
            onServicoToggle={(item) => { toggleSetItem(setServicoFilter, item); setNotasPage(1) }}
            onServicoClear={() => { setServicoFilter(new Set()); setNotasPage(1) }}
            uniqueServicos={uniqueServicos}
            anotacoesFilter={anotacoesFilter}
            onAnotacoesToggle={(item) => { toggleSetItem(setAnotacoesFilter, item); setNotasPage(1) }}
            onAnotacoesClear={() => { setAnotacoesFilter(new Set()); setNotasPage(1) }}
            valorBrutoFilter={valorBrutoFilter}
            valorBrutoBounds={valorBrutoBounds}
            onValorBrutoChange={(v) => { setValorBrutoFilter(v); setNotasPage(1) }}
            onValorBrutoClear={() => { setValorBrutoFilter(null); setNotasPage(1) }}
            valorLiquidoFilter={valorLiquidoFilter}
            valorLiquidoBounds={valorLiquidoBounds}
            onValorLiquidoChange={(v) => { setValorLiquidoFilter(v); setNotasPage(1) }}
            onValorLiquidoClear={() => { setValorLiquidoFilter(null); setNotasPage(1) }}
            retencaoFilter={retencaoFilter}
            retencaoBounds={retencaoBounds}
            onRetencaoChange={(v) => { setRetencaoFilter(v); setNotasPage(1) }}
            onRetencaoClear={() => { setRetencaoFilter(null); setNotasPage(1) }}
            emissaoRangeFilter={emissaoRangeFilter}
            onEmissaoRangeChange={(v) => { setEmissaoRangeFilter(v); setNotasPage(1) }}
            onEmissaoRangeClear={() => { setEmissaoRangeFilter(null); setNotasPage(1) }}
            vencimentoRangeFilter={vencimentoRangeFilter}
            onVencimentoRangeChange={(v) => { setVencimentoRangeFilter(v); setNotasPage(1) }}
            onVencimentoRangeClear={() => { setVencimentoRangeFilter(null); setNotasPage(1) }}
            baixaRangeFilter={baixaRangeFilter}
            onBaixaRangeChange={(v) => { setBaixaRangeFilter(v); setNotasPage(1) }}
            onBaixaRangeClear={() => { setBaixaRangeFilter(null); setNotasPage(1) }}
            activeChips={notaActiveChips}
            onRemoveChip={handleRemoveNotaChip}
            onClearAll={clearNotaFilters}
          />

          {/* Table with column chips */}
          <div className="bg-brand-dark-light border border-brand-dark-lighter rounded-monumenta overflow-hidden">
            {/* Column chips bar */}
            <ColumnChipsBar
              allColumns={NOTA_COLUMNS.map((c) => ({ key: c.key, label: c.label }))}
              visibleColumns={visibleNotaCols}
              onToggle={toggleNotaCol}
              presets={DEFAULT_NOTA_PRESETS}
              activePreset={activePreset}
              onSelectPreset={selectPreset}
              customPresets={customPresets}
              onSavePreset={saveCustomPreset}
              onDeletePreset={deleteCustomPreset}
            />

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-brand-dark-lighter">
                    {visibleNotaColumns.map((col) => {
                      const canHide = visibleNotaColumns.length > 1
                      return (
                        <th
                          key={col.key}
                          onClick={() => setNotasSort(toggleSort(notasSort, col.key))}
                          className={`group relative px-3 py-2.5 text-brand-gray/80 font-semibold text-[10px] uppercase tracking-wider cursor-pointer hover:text-white transition-colors select-none whitespace-nowrap ${col.width || ''} ${
                            col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                          }`}
                        >
                          <span className="inline-flex items-center">
                            {col.label}
                            <SortIndicator sortState={notasSort} columnKey={col.key} />
                            {canHide && (
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleNotaCol(col.key) }}
                                className="ml-1.5 p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-brand-dark-lighter text-brand-gray/60 hover:text-red-400 transition-opacity"
                                title="Ocultar coluna"
                                aria-label={`Ocultar coluna ${col.label}`}
                              >
                                <X size={11} />
                              </button>
                            )}
                          </span>
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {paginatedNotas.length === 0 ? (
                    <tr>
                      <td colSpan={visibleNotaColumns.length} className="px-3 py-12 text-center text-brand-gray">
                        Nenhuma nota encontrada com os filtros aplicados.
                      </td>
                    </tr>
                  ) : (
                    paginatedNotas.map((nota) => (
                      <tr
                        key={nota.id}
                        onClick={() => setSelectedNota(nota)}
                        className="border-b border-brand-dark-lighter/50 hover:bg-brand-dark-lighter/30 transition-colors cursor-pointer"
                      >
                        {visibleNotaColumns.map((col, colIdx) => (
                          <td
                            key={col.key}
                            className={`px-3 py-2 text-brand-gray whitespace-nowrap ${col.width || ''} ${
                              col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                            }`}
                          >
                            <span className="inline-flex items-center">
                              {col.render(nota)}
                              {colIdx === 0 && <AnnotationBadge id={nota.id} />}
                            </span>
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {filteredNotas.length > 0 && (
              <EnhancedPagination
                currentPage={notasPage}
                totalPages={notasTotalPages}
                totalRecords={filteredNotas.length}
                pageSize={pageSize}
                onPageChange={setNotasPage}
                onPageSizeChange={handlePageSizeChange}
                showJumpToPage={false}
                entityName="notas"
              />
            )}
          </div>

      {/* Detail Modal */}
      <NotaDetailModal
        open={!!selectedNota}
        onClose={() => setSelectedNota(null)}
        nota={selectedNota ?? undefined}
      />
    </div>
  )
}

export default CentralDeNotas
